<template>
  <div class="whitebackground tw-px-12 tw-pt-12 tw-pb-7">
    <p class="title is-1">
      Full Presentation
    </p>
    <p class="subtitle">
      Your presentation is being prepared automatically. It will download as a PowerPoint file once ready.
    </p>

    <div class="tw-flex tw-items-center tw-gap-3">
      <div v-if="isGeneratingZip">
        <i class="fa fa-spinner fa-spin" />
        <span class="tw-ml-2">Generating optimization package zip...</span>
      </div>
      <div v-else-if="isGenerating">
        <i class="fa fa-spinner fa-spin" />
        <span class="tw-ml-2">Generating presentation...</span>
      </div>
      <div v-else-if="generationComplete">
        <i
          class="fa fa-check-circle"
          style="color: green;"
        />
        <span class="tw-ml-2">Your presentation has been generated and downloaded.</span>
      </div>
      <div v-else-if="generationError">
        <i
          class="fa fa-exclamation-circle"
          style="color: red;"
        />
        <span class="tw-ml-2">There was an error generating your presentation.
          <a
            href="#"
            @click.prevent="downloadSlides"
          >Click here to try again</a>.
        </span>
      </div>
      <div v-else-if="!checkupStore.isCheckedPortfolios.length">
        <i
          class="fa fa-exclamation-circle"
          style="color: orange;"
        />
        <span class="tw-ml-2">Please select at least one portfolio to generate a presentation.</span>
      </div>
    </div>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { GRADE_COLORS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import pptxgen from 'pptxgenjs';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import JSZip from 'jszip';

import taxTransitionMethodology from '@frontend/ui/features/tax/taxTransitionMethodology.json';
import optimizerMethodology from '@frontend/assets/json/optimizer/optimizer_methodology_pptx.json';

const SLIDE_WIDTH = 8.5;
const SLIDE_HEIGHT = 11;
// Pixels per inch for images
// Since pptxgenjs uses inches, we need a way to convert.
// We don't know the PPI that pptxgenjs is assuming, so this is a guess. Seems to work okay, but adjust as needed.
const PPI = 150;

const MARGINS = {
  top: 1,
  bottom: 1,
  left: 0.5,
  right: 0.5,
  image: 0.1,
  header: {
    top: 0.25,
    bottom: 0.25,
    left: 0.25,
    right: 0.25,
  },
};

const DEFAULT_TEXT_CONFIG = {
  x: MARGINS.left, y: '50%',
  w: SLIDE_WIDTH - MARGINS.left - MARGINS.right,
  h: 0.25,
  align: 'left',
  fontSize: 12,
  fontFace: 'Poppins',
};


export default {
  name: 'OverviewArea',
  components: { },
  props: ['areas'],
  data() {
    return {
      GRADE_COLORS,
      y: 0,
      isGenerating: false,
      generationComplete: false,
      generationError: false,
      isGeneratingZip: false,
      slideDownloadTimeout: null,
      lastDownload: null,
    };
  },
  computed: {
    allAreasHaveExplanation() {
      return this.checkupStore.areas.every(area => area.explanation);
    },
    ...mapStores(useCheckupStore, useAdvisorStore),
  },
  watch: {
    allAreasHaveExplanation(newState) {
      if (newState) console.log('All areas have explanation');
    },
    'checkupStore.lastImageUpdates'(lastUpdates) {
      const recent = new Set();

      for (const name of Object.keys(lastUpdates || {})) {
        if (lastUpdates[name] > this.checkupStore.chartImageUpdateRequested) recent.add(name);
      }

      if (recent.has('Risk')) {
        this.scheduleSlideDownload();
      }
    },
    'checkupStore.zipExportRequested'(newVal) {
      if (newVal) {
        this.lastDownload = null;
        setTimeout(() => { this.checkupStore.chartImageUpdateRequested = Date.now(); }, 100);
      }
    },
  },
  mounted() {
    // NEW: Auto-trigger the download when the component mounts
    if (this.checkupStore.isCheckedPortfolios.length) {
      this.isGenerating = true;
      setTimeout(() => this.checkupStore.chartImageUpdateRequested = Date.now(), 500);
      // this.downloadSlides();
    }
  },
  methods: {
    // Helper function to normalize percentages and convert -0% to 0%
    normalizePercentage(value, decimalPlaces = 0) {
      let normalized = value.toFixed(decimalPlaces) + '%';
      if (normalized === '-0%') {
        normalized = '0%';
      }
      return normalized;
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    },
    getAccountValues() {
      const values = {};
      const items = this.checkupStore.portfolio?.uploaded_df_agg || [];
      items.forEach(item => {
        const accountName = item.account || 'Unknown';
        values[accountName] = (values[accountName] || 0) + (item.dollars || 0);
      });
      return values;
    },
    async getImageMetadata(src) {
      const img = new Image();
      img.src = src;
      await img.decode();
      return img;
    },
    htmlToPlainText(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.textContent || '';
    },

    async createCoverSlide(pres) {
      const coverSlide = pres.addSlide();

      this.addGeometricBackground(coverSlide);

      const textColor = '#000000'; // Black text

      const textConfig = {
        w: '90%',
        x: '5%',
        color: textColor,
        align: 'left',
        fontFace: 'Arial',
      };
      let date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) { // Ensure it's a valid date
          date = createdDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }
      }

      const title = this.advisorStore.firmAdvisorCoreSettings?.cover_page_title || 'Your Portfolio Analysis';

      coverSlide.addText(date, { ...textConfig, y: '32%', fontSize: 14 });
      coverSlide.addText(title, { ...textConfig, y: '37%', fontSize: 36, bold: true });
      coverSlide.addText(this.advisorStore.advisor.firm.name, { ...textConfig, y: '42%', fontSize: 20, bold: true });
      // coverSlide.addText(`Prepared by ${this.advisorStore.advisor.name}`, { ...textConfig, y: '45%', fontSize: 20 });


      try {
        await this.addCoverLogoWithTimeout(coverSlide);
      } catch (e) {
        console.log(e);
      }
      return pres;
    },
    addGeometricBackground(slide) {
      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#8C67EF';
      const secondaryColor = this.advisorStore.firmAdvisorCoreSettings?.benchmark_color || '#FF9F1C';
      // const accentColor = this.advisorStore.firmAdvisorCoreSettings?.proposed_color || '#CBF3F0';

      // Calculate darker shades
      const darkMainColor = this.darkenColor(mainColor, 20);
      const darkSecondaryColor = this.darkenColor(secondaryColor, 20);

      // Main background
      slide.background = { color: 'FFFFFF' }; // White background

      // Add geometric shapes
      const shapes = [
        { type: 'rect', x: 0, y: 0, w: '100%', h: '15%', fill: { color: darkMainColor } },
        { type: 'rect', x: 0, y: '15%', w: '100%', h: '5%', fill: { color: mainColor } },
        { type: 'rect', x: 0, y: '20%', w: '100%', h: '10%', fill: { color: mainColor } },
        { type: 'rect', x: 0, y: SLIDE_HEIGHT - 0.5, w: '100%', h: 0.5, fill: { color: darkSecondaryColor } },
      ];

      shapes.forEach(shape => {
        slide.addShape(shape.type, {
          x: shape.x,
          y: shape.y,
          w: shape.w,
          h: shape.h,
          fill: shape.fill,
        });
      });
    },
    addCoverLogoWithTimeout(slide) {
      // In theory we only need to wrap getImageMetadata with this since that is the only promise
      // But we are wrapping the entire addCoverLogo function just in case
      const timeout = new Promise((_, reject) => {
        const id = setTimeout(() => {
          clearTimeout(id);
          reject(new Error('Timed out while trying to add logo to slides'));
        }, 1500); // Adjust timeout here if needed
      });
      return Promise.race([this.addCoverLogo(slide), timeout]);
    },
    async addCoverLogo(slide) {
      try {
        const logoURL = this.advisorStore.advisor.firm.logo;
        if (!logoURL) return;
        const { naturalWidth, naturalHeight } = await this.getImageMetadata(logoURL);

        if (!this.advisorStore.firmLogoBase64) return;

        // Natural dims are in pixels, but pptxgenjs uses inches.
        const natural = this.pixelsToInches({ w: naturalWidth, h: naturalHeight });

        // Specify bounding box for logo
        const contain = this.scaleToBox(natural, {
          w: SLIDE_WIDTH * 0.25,
          h: SLIDE_HEIGHT * 0.1,
        });

        slide.addImage({
          data: `image/png;base64,${this.advisorStore.firmLogoBase64}`,
          x: SLIDE_WIDTH - contain.w - MARGINS.right,
          y: SLIDE_HEIGHT - contain.h - MARGINS.bottom,
          w: contain.w,
          h: contain.h,
        });
      } catch (e) {
        console.log('Error while trying to add logo to slides', e);
      }
    },
    scheduleSlideDownload() {
      window.clearTimeout(this.slideDownloadTimeout);
      this.slideDownloadTimeout = window.setTimeout(() => this.downloadSlides(), 2000);
    },
    async downloadSlides() {
      if (this.lastDownload && this.lastDownload > this.checkupStore.chartImageUpdateRequested) {
        // Already downloaded recently
        return;
      }
      this.lastDownload = Date.now();

      this.isGenerating = true;
      this.generationComplete = false;
      this.generationError = false;
      let pres = new pptxgen();
      pres.defineLayout({ name: 'LETTER', width: SLIDE_WIDTH, height: SLIDE_HEIGHT });
      pres.layout = 'LETTER';

      console.log('Creating cover slide');
      pres = await this.createCoverSlide(pres);
      console.log('Cover slide created');

      this.addDisclosureSlide(pres);

      console.log('These are ALL slideImages', this.checkupStore.slideImages);

      for (const area of this.checkupStore.filteredAreas) {
        const areaCharts = Object.values(this.checkupStore.charts[area.name] || {});
        const areaImages = Object.values(this.checkupStore.slideImages[area.name] || {});

        const chartImages = await this.getChartImages(areaCharts);
        const otherImages = this.getOtherImages(areaImages, area);

        // Add non-chart images (risk, etc)
        const slideImages = [...chartImages, ...otherImages];

        const chartType = area.chartData?.[0].chart?.type;

        if (area.name == 'Asset Location') {
          if (this.checkupStore.portfolio && this.checkupStore.portfolio?.uploaded_df_agg.length && this.checkupStore.portfolio?.uploaded_df_agg.some(item => ['Taxable', 'Tax Deferred', 'Tax Free'].includes(item.account_tax_status))) {
            this.addAssetLocationSlide(pres, area, slideImages);
            this.addAssetLocationTableSlide(pres, area);
            this.addPrinciplesSlide(pres, area);
          }
        } else if (chartType === 'pie') {
          // Handles splitting into multiple pages when table is too large
          this.addPieChartSection(pres, area, slideImages);
        } else {
          this.addChartSlide(pres, area, slideImages);
        }
      }

      if (this.checkupStore.optimization?.optimizations?.length && this.checkupStore.optimizedPortfolio?.isChecked) {
        this.addOptimizationSlide(pres);
      }

      if (
        this.checkupStore.portfolio &&
        this.checkupStore.optimizedPortfolio &&
        typeof this.checkupStore.optimizedPortfolio?.external_id === 'string' && this.checkupStore.optimizedPortfolio?.external_id.startsWith('entity_') &&
        this.checkupStore.includeSlides['Tax Transition']
      ) {
        this.addTaxTransitionSlide(pres);
      }
      if (this.checkupStore.featureFlags.includes('Feature: Stress Test') && this.checkupStore.includeSlides['Stress Test']) {
        this.addStressTestSlide(pres);
      }

      // TODO: Right now each area methodology is on a separate slide
      // Once we finish addTextBlock and can properly handle arbitrary multiline text,
      // We will be able to have multiple areas on one slide
      this.checkupStore.filteredAreas.forEach((area, i) => this.addMethodologySlide(pres, area));
      if (this.checkupStore.featureFlags.includes('Feature: Stress Test') && this.checkupStore.includeSlides['Stress Test']) {
        this.addStressTestMethodologySlide(pres);
      }
      if (this.checkupStore.includeSlides['Holdings']) {
        if (this.checkupStore.holdingsFromPortfolios && this.checkupStore.holdingsFromPortfolios.length > 0) {
          this.addHoldingsSlide(pres, 'portfolio');
        }

        // Add benchmark holdings slide if they exist
        if (
          this.checkupStore.benchmark &&
          this.checkupStore.benchmark.holdings &&
          this.checkupStore.benchmark.holdings.length > 0
        ) {
          this.addHoldingsSlide(pres, 'benchmark');
        }

        // Add proposed holdings slide if they exist
        if (
          this.checkupStore.optimizedPortfolio &&
          this.checkupStore.optimizedPortfolio.holdings &&
          this.checkupStore.optimizedPortfolio.holdings.length > 0
        ) {
          this.addHoldingsSlide(pres, 'proposed');
        }
      }
      if (this.checkupStore.zipExportRequested) {
        console.log('Generating zip package');
        this.isGeneratingZip = true;
        try {
          const pptxBuffer = await pres.write({ outputType: 'uint8array' });
          await this.generateZipPackage(pptxBuffer);
        } catch (e) {
          console.error('Error generating zip package', e);
          this.generationError = true;
        } finally {
          this.isGeneratingZip = false;
          this.isGenerating = false;
          this.checkupStore.zipExportRequested = false;
          this.checkupStore.optimizerSettingsDataUrl = null;
        }
      } else {
        console.log('Writing file');
        pres.writeFile({ fileName: 'Sample Presentation.pptx' });
        console.log('File written');
        this.isGenerating = false;
        this.generationComplete = true;
        setTimeout(() => { this.generationComplete = false; }, 5000);
      }
    },
    generateTaxLotCsv(idx) {
      let taxLotTrades = this.checkupStore.optimization.optimizations[idx].diagnostics.tax_lot_trades;
      const buyList = this.checkupStore.optimization.optimizations[idx].buy_list || this.checkupStore.optimization.optimizations[idx].diagnostics?.buy_list;
      const buyListTickers = buyList?.map(item => item.ticker__symbol) || [];

      const KNOWN_COLUMNS = ['Security', 'Initial Amount', 'Trade Amount', 'Long Term Capital Gain/Loss', 'Short Term Capital Gain/Loss'];
      const topKeys = Object.keys(taxLotTrades);
      if (topKeys.length === KNOWN_COLUMNS.length && KNOWN_COLUMNS.every(c => topKeys.includes(c))) {
        taxLotTrades = { 'Default': taxLotTrades };
      }

      const csvCell = v => {
        const s = v != null ? String(v) : '';
        return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
      };

      const firstAccount = Object.keys(taxLotTrades)[0];
      const tradeColumns = Object.keys(taxLotTrades[firstAccount] || {});
      let csv = ['Account', ...tradeColumns, 'On Buy List'].map(csvCell).join(',') + '\n';
      Object.entries(taxLotTrades).forEach(([accountName, accountTrades]) => {
        const rows = Object.keys(accountTrades[tradeColumns[0]] || {});
        rows.forEach(row => {
          const values = tradeColumns.map(col => csvCell(accountTrades[col]?.[row]));
          const security = accountTrades['Security']?.[row] || '';
          csv += [csvCell(accountName), ...values, buyListTickers.includes(security) ? 'Yes' : 'No'].join(',') + '\n';
        });
      });
      return csv;
    },
    async resolveScreensetsToMetricIDs(screensetIDs) {
      // The metric ids are usually already in the store, but selectedValues arrives in one
      // of two shapes depending on where the selection came from:
      //   /api/v1/screen_sets/                      -> metrics[] are thresholds: { metric_description }
      //   /api/v1/metric_descriptions/?screen_sets= -> metrics[] are descriptions: { id }
      // (the latter is the ?values= URL path, i.e. a reloaded or shared checkup)
      const selected = this.checkupStore.selectedValues || [];
      const perValueIDs = selected.map(value => (value.metrics || [])
          .map(metric => metric.metric_description ?? metric.id)
          .filter(id => id != null));
      // Only trust the store when every selected value contributed at least one metric;
      // a partially-populated selection would silently under-report.
      if (selected.length && perValueIDs.every(ids => ids.length)) {
        return [...new Set(perValueIDs.flat())];
      }

      try {
        const resp = await fetch(`/api/v1/metric_descriptions/?screen_sets=${screensetIDs.join(',')}`);
        if (!resp.ok) {
          console.warn('Could not resolve screensets to metric descriptions; skipping impact report');
          return null;
        }
        const data = await resp.json();
        return [...new Set(data.flatMap(ss => (ss.metrics || []).map(m => m.id)))];
      } catch (e) {
        console.warn('Could not resolve screensets to metric descriptions', e);
        return null;
      }
    },
    async generateImpactReportPdf() {
      const screensetIDs = this.checkupStore.selectedValueIds || [];
      if (!screensetIDs.length) return null;

      const portfolioForReport = this.checkupStore.optimizedPortfolio || this.checkupStore.portfolio;
      const hasOptimization = !!this.checkupStore.optimization;
      // Impact report URL uses entity external_id (entity_XXXX) — Entity.slug == Entity.external_id
      const portfolioEntityId = (hasOptimization && portfolioForReport?.equity_only_entity_id) ?
        portfolioForReport.equity_only_entity_id :
        portfolioForReport?.external_id;
      if (!portfolioEntityId) return null;
      const benchmarkEntityId = (hasOptimization && this.checkupStore.benchmark?.equity_only_entity_id) ?
        this.checkupStore.benchmark.equity_only_entity_id :
        this.checkupStore.benchmark?.external_id;

      // `screens=` expects MetricDescription ids, not MetricScreenSet ids: generate_report()
      // resolves it via MetricDescription.objects.filter(pk__in=...). selectedValueIds holds
      // screenset ids, so passing them straight through reported whichever unrelated metrics
      // happened to share those pks. Resolve the selected screensets to their metrics first.
      const metricIDs = await this.resolveScreensetsToMetricIDs(screensetIDs);
      if (!metricIDs?.length) return null;

      const slugPair = benchmarkEntityId ? `${portfolioEntityId}:${benchmarkEntityId}` : portfolioEntityId;
      const params = new URLSearchParams({ screens: metricIDs.join(','), report_type: 'minimal_relative' });
      const reportUrl = `/impact-report/${slugPair}/?${params.toString()}`;

      let taskId;
      try {
        const resp = await fetch('/api/v1/process/report/download_pdf/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.$root.$data.csrfToken,
          },
          body: JSON.stringify({ report_url: reportUrl }),
        });
        if (!resp.ok) return null;
        const data = await resp.json();
        taskId = data.task?.id;
        if (!taskId) return null;
      } catch (e) {
        console.warn('Could not initiate impact report PDF generation', e);
        return null;
      }

      try {
        await new Promise((resolve, reject) => {
          const check = async () => {
            const resp = await fetch(`/report-status/${taskId}/`);
            if (!resp.ok) {
              reject(new Error('report status check failed'));
              return;
            }
            const data = await resp.json();
            if (data.status === 'complete') {
              resolve();
              return;
            }
            if (data.status === 'error') {
              reject(new Error('PDF generation failed'));
              return;
            }
            setTimeout(check, 2000);
          };
          check().catch(reject);
        });
      } catch (e) {
        console.warn('Impact report PDF generation failed', e);
        return null;
      }

      try {
        const resp = await fetch(`/download-report/${taskId}/`);
        if (!resp.ok) return null;
        return await resp.arrayBuffer();
      } catch (e) {
        console.warn('Could not download impact report PDF', e);
        return null;
      }
    },
    async generateZipPackage(pptxBuffer) {
      const zip = new JSZip();

      zip.file('presentation.pptx', pptxBuffer);

      if (this.checkupStore.optimizerSettingsDataUrl) {
        const base64Data = this.checkupStore.optimizerSettingsDataUrl.split(',')[1];
        zip.file('optimizer_settings.png', base64Data, { base64: true });
      }

      const optimizations = this.checkupStore.optimization?.optimizations || [];
      for (let i = 0; i < optimizations.length; i++) {
        const o = optimizations[i];
        const slug = o?.optimization_portfolio_slug || o?.optimization_portfolio?.slug;
        if (slug) {
          try {
            const resp = await fetch(`/reportholdingscsv/${slug}/`);
            if (resp.ok) {
              const csvText = await resp.text();
              const safeName = (o.optimization_portfolio_name || `portfolio_${i}`).replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_').substring(0, 60);
              zip.file(`optimized_holdings_${safeName}.csv`, csvText);
            }
          } catch (e) {
            console.warn('Could not fetch holdings CSV for', slug, e);
          }
        }

        if (this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')) {
          for (const acctPort of o.account_portfolios || []) {
            if (acctPort.slug) {
              try {
                const resp = await fetch(`/reportholdingscsv/${acctPort.slug}/`);
                if (resp.ok) {
                  const csvText = await resp.text();
                  const safeName = (acctPort.name || 'account').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_').substring(0, 60);
                  zip.file(`account_holdings_${safeName}.csv`, csvText);
                }
              } catch (e) {
                console.warn('Could not fetch account holdings CSV', e);
              }
            }
          }

          if (o?.diagnostics?.tax_lot_trades && Object.keys(o.diagnostics.tax_lot_trades).length) {
            const csv = this.generateTaxLotCsv(i);
            const safeName = (o.optimization_portfolio_name || `portfolio_${i}`).replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_').substring(0, 60);
            zip.file(`tax_lot_trades_${safeName}.csv`, csv);
          }
        }
      }

      if (this.advisorStore.advisor?.firm?.name === 'Formative') {
        const buyList = optimizations[0]?.buy_list;
        if (buyList?.length) {
          let csv = 'Ticker,Dollars\n';
          buyList.forEach(item => { csv += `${item.ticker__symbol},1\n`; });
          zip.file('buy_list.csv', csv);
        }
      }

      const impactReportPdf = await this.generateImpactReportPdf();
      if (impactReportPdf) {
        zip.file('impact_report.pdf', impactReportPdf);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = 'optimization_package.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
    // Clean up raw HTML text content
    cleanTextContent(content) {
      if (!content) return '';
      const clean = content.replace(/\n+/g, ' ').replace(/ +/g, ' ');
      return clean.trim();
    },
    // Calculates the line height to be used for the textbox
    // Adjust values as needed
    getLineHeight(pixels) {
      return (pixels * 1.3) / 72; // 72 pixels per inch
    },
    getCharWidth(pixels) {
      return (pixels * 0.7) / 72; // Rough estimate of character width
    },
    pixelsToInchesText(pixels) {
      // For text only
      // We may use a different value for images (see PPI above)
      return pixels / 72;
    },
    // Adds a single line of text to the slide and increments the shared y position
    addTextLine(slide, text, otherSettings={}, updateY=true) {
      if (!otherSettings.fontSize) otherSettings.fontSize = 12;
      const y = otherSettings.y || this.y;

      const config = {
        ...DEFAULT_TEXT_CONFIG,
        ...otherSettings,
        y,
      };
      config.h = this.getLineHeight(config.fontSize);

      slide.addText(text, config);
      if (updateY) this.y = this.y + config.h;
    },
    addTextBlock(slide, text, otherSettings={}, updateY=true) {
      if (!otherSettings.fontSize) otherSettings.fontSize = 12;
      const y = otherSettings.y || this.y;

      const config = {
        ...DEFAULT_TEXT_CONFIG,
        ...otherSettings,
        y,
        valign: 'top',
      };
      config.h = this.getBlockHeight(text, config.fontSize, config.w);

      slide.addText(text, config);
      if (updateY) this.y = this.y + config.h;
    },
    getBlockHeight(text, fontSize, width) {
      const charWidth = this.getCharWidth(fontSize); // Rough estimate of character width in inches

      // Estimate how many characters can fit on one line
      const lineLength = Math.floor(width / charWidth);
      // console.log('getBlockHeight.text:', text);
      // console.log('getBlockHeight.lineLength:', lineLength);

      const lines = [];
      const words = text.split(' ');

      let line = '';
      // Loop through each word
      while (words.length > 0) {
        const word = words.shift();

        // If adding the next word would exceed the line length, start a new line.
        if (line.length + word.length > lineLength) {
          lines.push(line);
          line = word + ' ';
          //
        } else {
          // Otherwise just add the word to the current line
          line = line + word + ' ';
        }
      }

      // Now lines.length is the estimated number of lines with wrapping
      return this.getLineHeight(fontSize) * Math.max(lines.length, 1);
    },
    addTextList(slide, items, otherSettings={}, itemSettings={}, updateY=true) {
      if (!otherSettings.fontSize) otherSettings.fontSize = 10;
      const y = otherSettings.y || this.y;

      const itemConfig = {
        breakLine: true,
        bullet: { type: 'number' },
        ...itemSettings,
      };
      const itemsWithConfig = items.map(item => ({ text: item, options: itemConfig }));

      // const lineHeight = this.getLineHeight(otherSettings.fontSize + spaceAfter);
      // const height = (lineHeight) * items.length;

      const sharedConfig = {
        ...DEFAULT_TEXT_CONFIG,
        ...otherSettings,
        valign: 'top',
        y,
      };

      const spaceAfter = this.pixelsToInchesText(Math.ceil(sharedConfig.fontSize * 0.5));
      sharedConfig.paraSpaceAfter = spaceAfter;

      sharedConfig.h = items.reduce((total, text) => {
        const itemHeight = this.getBlockHeight(text, sharedConfig.fontSize, sharedConfig.w);
        return total + itemHeight + spaceAfter;
      }, 0);
      console.log('addTextList.sharedConfig.h', sharedConfig.h);

      slide.addText(itemsWithConfig, sharedConfig);
      if (updateY) this.y = this.y + sharedConfig.h;
    },
    addSlideHeader(slide, page, totalPages) {
      this.y = MARGINS.header.top;
      const baseConfig = { fontSize: 10, color: '#b3c6b9' };

      let date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) { // Ensure it's a valid date
          date = createdDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }
      }

      this.addTextLine(
          slide,
          `${date}`,
          {
            ...baseConfig,
            align: 'right',
            x: SLIDE_WIDTH - MARGINS.header.right,
          },
          false,
      );
      this.addTextLine(
          slide,
          'Investment Proposal',
          {
            ...baseConfig,
            align: 'right',
            w: (SLIDE_WIDTH / 2) - MARGINS.header.right,
            x: (SLIDE_WIDTH / 2),
          },
      );
    },
    addSlideFooter(slide) {
      this.y = SLIDE_HEIGHT - MARGINS.header.bottom;
      const baseConfig = { fontSize: 10, color: '#b3c6b9' };

      this.addTextLine(
          slide,
          `Presented by ${this.advisorStore.advisor.firm.name}`,
          {
            ...baseConfig,
            align: 'right', valign: 'bottom',
            x: MARGINS.header.right,
          },
          false,
      );
      // this.addTextLine(
      //     slide,
      //     this.advisorStore.advisor.name,
      //     {
      //       ...baseConfig,
      //       align: 'right', valign: 'bottom',
      //       w: (SLIDE_WIDTH / 2) - MARGINS.header.right,
      //       x: (SLIDE_WIDTH / 2),
      //     },
      // );
    },
    addSlideTitle(slide, title, options={}) {
      this.y+= 0.15; // TODO: This vertical margin logic should be moved to addTextLine
      this.addTextLine(slide, title, { fontSize: 36, align: 'left', bold: true, fontFace: 'Georgia', ...options });
      this.y+= 0.15;
    },
    addStripeGraphic(slide) {
      const assets = this.$root.$data.STATIC_ROOT;

      slide.addImage({
        path: `${assets}sillyimages/advisorcore_stripes.svg`,
        x: MARGINS.left + 0.11,
        y: this.y - 0.13,
        h: 0.1,
      });
    },
    getGradeColor(area) {
      return GRADE_COLORS[area.grade]?.hexColor || '#808080';
    },
    addDisclosureSlide(pres) {
      let date = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

      if (this.checkupStore.portfolio_checkup_created_date) {
        const createdDate = new Date(this.checkupStore.portfolio_checkup_created_date);
        if (!isNaN(createdDate)) { // Ensure it's a valid date
          date = createdDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
        }
      }

      const YOURSTAKE_DISCLAIMER = [
        'Stake PBC is not an investment advisor, and does not provide investment advice or investment recommendations.',
        'The information contained herein is believed to be from reliable sources, but we make no warranty or guarantee as to the accuracy of the information.',
        `Information provided is as of ${date}, and may change over time based on market or other conditions.`,
        'Investing involves risk.',
        'Depending on the types of investments, there may be varying degrees of risk.',
      ].join(' ');
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      this.addSlideTitle(slide, 'Disclosures');
      this.addTextLine(slide, 'YourStake Disclaimer', { fontSize: 12, bold: true });
      this.addTextBlock(slide, YOURSTAKE_DISCLAIMER, { fontSize: 10, valign: 'top' });

      const firmDisclosure = this.advisorStore.advisor?.firm?.advisorcore_compliance_language;
      if (!firmDisclosure) return;

      this.y += 1; // TODO: We won't need this once we finish addTextBlock
      this.addTextLine(slide, `${this.advisorStore.advisor.firm.name} Disclosures`, { fontSize: 12, bold: true });
      this.addTextBlock(slide, firmDisclosure || 'No firm specific disclosures', { fontSize: 10, valign: 'top' });
    },
    truncate(text, maxLength) {
      if (text.endsWith('Avg Expense Ratio')) {
        const prefix = text.replace(/ Avg Expense Ratio$/, ''); // Removes the suffix
        const suffix = ' Avg Expense Ratio';
        return prefix.length > maxLength ? prefix.substring(0, maxLength) + '...' + suffix : text;
      } else if (text.endsWith('Advisory and Mgmt Fees')) {
        const prefix = text.replace(/ Advisory and Mgmt Fees$/, ''); // Removes the suffix
        const suffix = ' Advisory and Mgmt Fees';
        return prefix.length > maxLength ? prefix.substring(0, maxLength) + '...' + suffix : text;
      } else {
        // If no special text is found, just truncate the whole text
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
      }
    },
    addFeesTitle(slide, title, area) {
      this.y+= 0.15; // TODO: This vertical margin logic should be moved to addTextLine
      const headerY = this.y;
      if (area.cumulative_fee_difference) {
        const savingsText = isNaN(area.cumulative_fee_difference) ? `${Math.abs(area.cumulative_fee_difference)}` : `${area.cumulative_fee_difference.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}`;
        this.addTextLine(slide, '20-yr Difference', {
          align: 'right',
          x: MARGINS.header.left,
        });
        this.addTextBlock(slide, savingsText, {
          fontSize: 18,
          bold: true,
          align: 'right',
          x: MARGINS.header.left,
          y: this.y,
        });
      }

      this.y = headerY;
      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#40B6D8';
      this.addTextLine(slide, title, { fontSize: 36, align: 'left', bold: true, fontFace: 'Georgia', color: mainColor });
      this.y+= 0.15;
    },

    addAssetLocationSlide(pres, area, slideImages) {
      function processChartData(chartData, slideImages, rowType, offsetY) {
        if (!chartData || chartData.length === 0) return offsetY;

        chartData.forEach(chart => {
          const chartType = chart?.chart?.type;
          if (chartType === 'pie' && chart.series && chart.series.length > 0) {
            const labels = chart.series.map(s => `${s.name}`);
            slideImages.forEach(img => {
              if (img.rowType === rowType) {
                if (!img.labels) img.labels = [];
                img.labels = [...img.labels, ...labels];
              }
            });
          }
        });

        return offsetY + chartData.length * 2; // Adjust Y position based on chart height
      }

      function collectLegendData(chartData) {
        const types = new Set();
        if (chartData) {
          chartData.forEach(chart => {
            if (chart.series) {
              chart.series.forEach(series => {
                series.data.forEach(point => {
                  types.add(point.name);
                });
              });
            }
          });
        }
        return Array.from(types);
      }

      function getLegendColor(type) {
        const colors = {
          'US Stocks': '#4A90E2',
          'International Stocks': '#FF6B6B',
          'Taxable Bonds': '#FF8C00',
          'Municipal Bonds': '#008080',
          'Cash': '#4CAF50',
          'Alternatives': '#9B51E0',
          'Unclassified': '#BDBDBD',
        };
        return colors[type] || '#BDBDBD'; // Default color if type not found
      }

      const slide = pres.addSlide();
      this.y = 0; // Reset vertical position for the slide
      this.addSlideHeader(slide, 1, 1);

      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#40B6D8';
      const title = area.name.replace('Breakdown', '').trim();
      this.addSlideTitle(slide, title, { color: mainColor });

      if (area.headerText) {
        this.addTextBlock(slide, area.headerText, { fontSize: 10, valign: 'top' });
        this.y += 0.25; // Move position down after header text
      }

      // Add Current Allocation text and charts
      if (area.portfolioChartData && area.portfolioChartData.some(chart => chart.series?.length)) {
        slide.addText(`Current Allocation`, {
          x: 0,
          y: this.y,
          w: SLIDE_WIDTH,
          fontSize: 16,
          align: 'center',
          bold: true,
        });
        this.y += 0.3;

        const portfolioName = this.checkupStore.portfolio?.name || 'Portfolio';
        slide.addText(portfolioName, {
          x: 0,
          y: this.y,
          w: SLIDE_WIDTH,
          fontSize: 12,
          align: 'center',
          color: 'gray',
        });
        this.y += 0.5;

        slideImages.forEach(img => {
          if (img.rowType === 'portfolio') {
            img.y = this.y; // Adjust image position
          }
        });

        this.y = processChartData(area.portfolioChartData, slideImages, 'portfolio', this.y);
      } else {
        this.y += 2;
      }

      // Add Benchmark charts
      slideImages.forEach(img => {
        if (img.rowType === 'benchmark') {
          img.y = this.y; // Adjust image position
        }
      });

      if (area.benchmarkChartData && area.benchmarkChartData.some(chart => chart.series?.length)) {
        this.y += 3; // Space between sections
        slide.addText(`Recommended Allocation`, {
          x: 0,
          y: this.y,
          w: SLIDE_WIDTH,
          fontSize: 16,
          bold: true,
          align: 'center',
        });
        this.y += 0.3;

        const benchmarkName = this.checkupStore.benchmark?.name || 'Benchmark';
        slide.addText(benchmarkName, {
          x: 0,
          y: this.y,
          w: SLIDE_WIDTH,
          fontSize: 12,
          align: 'center',
          color: 'gray',
        });

        this.y = processChartData(area.benchmarkChartData, slideImages, 'benchmark', this.y);
      }

      // Add all images to the slide
      const locationType = true;
      this.addImagesColumn(slide, slideImages, locationType);

      // Add legend
      const legendData = Array.from(
          new Set(collectLegendData(area.portfolioChartData).concat(collectLegendData(area.benchmarkChartData))),
      );
      if (legendData.length > 0) {
        const legendY = SLIDE_HEIGHT - 1.5; // Fixed position above methodology
        const legendXStart = 0.35; // Starting x position for the legend
        const legendItemWidth = 1.2; // Width allocated for each legend item
        const rectSize = 0.2; // Smaller rectangle for the legend
        const fontSize = 7; // Smaller font size for the labels

        legendData.forEach((type, index) => {
          const x = legendXStart + (index * legendItemWidth); // Calculate x position for each item
          slide.addShape(pres.ShapeType.rect, {
            x,
            y: legendY, // Fixed y position for all legend items
            w: rectSize,
            h: rectSize,
            fill: { color: getLegendColor(type) },
          });
          slide.addText(type, {
            x: x + rectSize + 0.1, // Position the text to the right of the rectangle
            y: legendY,
            w: legendItemWidth - rectSize - 0.2, // Adjust width for text
            fontSize,
            valign: 'middle', // Align text vertically to the rectangle
          });
        });
      }

      const startingBottomTextY = SLIDE_HEIGHT - 1;
      this.y = startingBottomTextY;

      // Footer text block
      this.addTextLine(slide, 'Methodology', { fontSize: 10, bold: true, w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });
      const shortMethodologyText = this.checkupStore.methodologyData[area.methodology].short;
      const cleanMethodology = this.cleanTextContent(shortMethodologyText);
      this.addTextBlock(slide, cleanMethodology, { fontSize: 8, valign: 'top', w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });

      if (area.explanation) {
        const explanationText = this.htmlToPlainText(area.explanation);
        slide.addNotes(explanationText);
      }
    },
    addAssetLocationTableSlide(pres, area) {
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#40B6D8';
      this.addSlideTitle(slide, 'Asset Location Breakdown', { color: mainColor });
      this.addStripeGraphic(slide);
      this.y += 0.1;

      // Gather tax statuses from chart series
      const taxStatuses = new Set();
      const addFromChartData = (chartData) => {
        if (chartData?.[0]?.series) {
          chartData[0].series.forEach(series => taxStatuses.add(series.name));
        }
      };
      addFromChartData(area.portfolioChartData);
      addFromChartData(area.optimizedPortfolioChartData);
      const statuses = Array.from(taxStatuses);

      // Gather all asset classes from chart series
      const assetClassSet = new Set();
      const addAssetClasses = (chartData) => {
        if (chartData?.[0]?.series) {
          chartData[0].series.forEach(series => {
            series.data.forEach(point => assetClassSet.add(point.name));
          });
        }
      };
      addAssetClasses(area.portfolioChartData);
      addAssetClasses(area.optimizedPortfolioChartData);
      const assetClasses = Array.from(assetClassSet);

      const hasPortfolio = area.portfolioChartData?.[0]?.series?.length > 0;
      const hasOptimized = area.optimizedPortfolioChartData?.[0]?.series?.length > 0;

      // Compute tax status dollar totals from uploaded_df_agg
      const computeTaxValues = (items) => {
        const values = {};
        (items || []).forEach(item => {
          const status = item.account_tax_status || 'Unclassified';
          values[status] = (values[status] || 0) + (parseFloat(item.dollars) || 0);
        });
        return values;
      };

      // Compute distinct account counts per tax status
      const computeAccountCounts = (items) => {
        const byStatus = {};
        (items || []).forEach(item => {
          const status = item.account_tax_status || 'Unclassified';
          const account = item.account || 'Unknown';
          if (!byStatus[status]) byStatus[status] = new Set();
          byStatus[status].add(account);
        });
        const counts = {};
        for (const [status, accounts] of Object.entries(byStatus)) {
          counts[status] = accounts.size;
        }
        return counts;
      };

      const portfolioValues = computeTaxValues(this.checkupStore.portfolio?.uploaded_df_agg);
      const optimizedValues = computeTaxValues(this.checkupStore.optimizedPortfolio?.uploaded_df_agg);
      const portfolioAccountCounts = computeAccountCounts(this.checkupStore.portfolio?.uploaded_df_agg);
      const optimizedAccountCounts = computeAccountCounts(this.checkupStore.optimizedPortfolio?.uploaded_df_agg);

      const getSeriesByName = (chartData, name) => {
        if (!chartData?.[0]?.series) return null;
        return chartData[0].series.find(s => s.name === name) || null;
      };

      const getPercentage = (series, assetClass) => {
        if (!series) return '0%';
        const pt = series.data.find(p => p.name === assetClass);
        if (!pt) return '0%';
        let val = (pt.y * 100).toFixed(0) + '%';
        if (val === '-0%') val = '0%';
        return val;
      };

      const cellStyle = { align: 'center', valign: 'center', fontSize: 8, border: { pt: '1', color: '000000' } };
      const headerStyle = { ...cellStyle, bold: true, fill: { color: 'F3F4F6' } };
      const boldCellStyle = { ...cellStyle, bold: true, fill: { color: 'F3F4F6' } };

      // Build sub-columns per status
      const subCols = [];
      statuses.forEach(status => {
        if (hasPortfolio) subCols.push({ status, type: 'current' });
        if (hasOptimized) subCols.push({ status, type: 'proposed' });
      });

      // Header row 1: blank + tax status name centered over its sub-columns, empty for the rest
      const headerRow1 = [
        { text: '', options: headerStyle },
      ];
      statuses.forEach(status => {
        const colsForStatus = (hasPortfolio ? 1 : 0) + (hasOptimized ? 1 : 0);
        if (colsForStatus === 1) {
          headerRow1.push({ text: status, options: headerStyle });
        } else {
          // Put name in first sub-col, leave second blank
          headerRow1.push({ text: status, options: headerStyle });
          headerRow1.push({ text: '', options: headerStyle });
        }
      });

      // Header row 2: "Asset Class" + Current/Proposed sub-headers
      const headerRow2 = [
        { text: 'Asset Class', options: headerStyle },
      ];
      statuses.forEach(() => {
        if (hasPortfolio) headerRow2.push({ text: 'Current', options: headerStyle });
        if (hasOptimized) headerRow2.push({ text: 'Proposed', options: headerStyle });
      });

      // Total Value row
      const totalValueRow = [
        { text: 'Total Value', options: boldCellStyle },
      ];
      statuses.forEach(status => {
        if (hasPortfolio) totalValueRow.push({ text: this.formatCurrency(portfolioValues[status] || 0), options: boldCellStyle });
        if (hasOptimized) totalValueRow.push({ text: this.formatCurrency(optimizedValues[status] || 0), options: boldCellStyle });
      });

      // Account count row
      const accountCountRow = [
        { text: '# of Accounts', options: boldCellStyle },
      ];
      statuses.forEach(status => {
        if (hasPortfolio) accountCountRow.push({ text: String(portfolioAccountCounts[status] || 0), options: boldCellStyle });
        if (hasOptimized) accountCountRow.push({ text: String(optimizedAccountCounts[status] || 0), options: boldCellStyle });
      });

      // Asset class rows
      const assetClassRows = assetClasses.map(assetClass => {
        const row = [
          { text: assetClass, options: { ...cellStyle, align: 'left', bold: true } },
        ];
        statuses.forEach(status => {
          if (hasPortfolio) {
            row.push({ text: getPercentage(getSeriesByName(area.portfolioChartData, status), assetClass), options: cellStyle });
          }
          if (hasOptimized) {
            row.push({ text: getPercentage(getSeriesByName(area.optimizedPortfolioChartData, status), assetClass), options: cellStyle });
          }
        });
        return row;
      });

      const tableData = [headerRow1, headerRow2, totalValueRow, accountCountRow, ...assetClassRows];

      const totalCols = 1 + subCols.length;
      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const firstColWidth = 1.2;
      const remainingWidth = tableWidth - firstColWidth;
      const colW = [firstColWidth, ...Array(totalCols - 1).fill(remainingWidth / (totalCols - 1))];

      slide.addTable(tableData, {
        x: MARGINS.left,
        y: this.y,
        w: tableWidth,
        colW,
        fontSize: 8,
        border: { pt: '1', color: '000000' },
        valign: 'middle',
      });

      this.addSlideFooter(slide);
    },
    addTaxTransitionSlide(pres) {
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      const title = 'Tax Transition Plan';
      this.addSlideTitle(slide, title);
      this.addStripeGraphic(slide);
      this.y += 0.1;

      // Intro
      this.addTextLine(slide, 'What is a tax transition?', { bold: true });
      this.addTextBlock(slide, 'A tax transition is a plan to move your investments from one portfolio to another in a tax-efficient manner.', { fontSize: 10 });
      this.y += 0.5;

      // Why are tax transitions important?
      this.addTextLine(slide, 'Why are tax transitions important?', { bold: true });
      this.addTextLine(slide, 'Tax transitions help minimize your tax burden through three main strategies:', { fontSize: 8 });

      this.addTextList(slide, [
        'Minimal Taxes: Aim to avoid any tax costs whatsoever, by spreading out transition over time to always match capital gains with loss offsets.',
        'Gradual Budgeted: Partially transition the portfolio each year, up to a certain threshold of tax costs that you budget. This may come from tax offsets elsewhere in your financial plan.',
        'Full Transition Today: Sell all current holdings into the new portfolio this year. Potentially to pay high taxes upfront.',
      ], { fontSize: 8 });
      this.y += 0.3;

      // Tradeoffs
      this.addTextLine(slide, 'Trade-offs in Tax Transitions', { bold: true });
      this.addTextLine(slide, 'Choosing a transition strategy involves considering:', { fontSize: 8 });

      this.addTextList(slide, [
        'Tracking Error: The difference between your starting and destination portfolio.',
        'Time: How long the transition will take.',
        'Tax Costs: The total taxes incurred during the transition.',
      ], { fontSize: 8 });

      this.addTextLine(slide, 'We will show you the tradeoffs between tracking error, time, and the tax costs in all three scenarios.', { fontSize: 8 });
      this.y += 0.3;

      // Portfolio names
      const boxWidth = (SLIDE_WIDTH - MARGINS.left - MARGINS.right - 0.5) / 2;

      const boxConfig = {
        x: MARGINS.left,
        y: this.y,
        w: boxWidth,
        h: 0.85,
        valign: 'top',
        fontSize: 10,
        paraSpaceAfter: 5,
        line: { width: '1', color: '808080' },
      };

      // Starting portfolio
      slide.addText([
        { text: 'Starting Portfolio:', options: { breakLine: true, bold: true } },
        { text: this.checkupStore.portfolio?.name, options: {} },
      ], boxConfig);

      // Destination portfolio
      slide.addText([
        { text: 'Destination Portfolio:', options: { breakLine: true, bold: true } },
        { text: this.checkupStore.optimizedPortfolio?.name, options: {} },
      ], { ...boxConfig, x: MARGINS.left + boxWidth + 0.5 });

      // Table
      this.y += 1.5;
      const rows = this.checkupStore.taxTransitionPlan;

      // Define table options
      const tableOptions = {
        x: MARGINS.left,
        y: this.y,
        w: SLIDE_WIDTH - MARGINS.left - MARGINS.right,
        fontSize: 10,
        border: { pt: '1', color: '808080' },
      };

      // Add fill and bold to the top row, bold to the first column
      if (rows?.length) {
        const formattedRows = rows.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const cellOptions = {};

            // Top row (header row)
            if (rowIndex === 0) {
              cellOptions.bold = true;
              cellOptions.fill = 'F3F4F6'; // Add fill to header row
            }

            // First column
            if (colIndex === 0) {
              cellOptions.bold = true; // Bold first column
            }

            return {
              text: cell,
              options: cellOptions,
            };
          }),
        );

        slide.addTable(formattedRows, tableOptions);
      }

      // Methodology
      this.y += 2;
      this.addTextLine(slide, 'Understanding the table methodology', { bold: true });
      const methodology = taxTransitionMethodology.map(text => ({
        text,
        options: { breakLine: true },
      }));

      slide.addText(methodology, {
        ...DEFAULT_TEXT_CONFIG,
        y: this.y,
        valign: 'top',
        fontSize: 8,
        paraSpaceAfter: 5,
        color: '666666',
      });
    },
    addStressTestSlide(pres) {
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      const title = 'Stress Test Results';
      this.addSlideTitle(slide, title);
      this.addStripeGraphic(slide);
      this.y += 0.1;

      // Intro
      this.addTextBlock(slide, 'Portfolio stress tests show how a portfolio, as it is currently constructed, might have performed under various market time frames, such as the 2008 financial crisis or the COVID-19 market downturn. While stress tests do not predict the future, they help highlight how a portfolio could react to scenarios resembling those that have occurred in the past, such as interest rate spikes, market crashes, or periods of high inflation. Stress tests are a tool for understanding risk exposure, but they are not guarantees of future outcomes.', { fontSize: 10, valign: 'top' });
      this.y += 0.5;

      // Add chart if available - similar to how addChartSlide works
      if (this.checkupStore.stressTestChartData?.chartOptions) {
        // Create a mock area object for the stress test chart
        const stressTestArea = {
          name: 'Stress Test',
          chartData: [{ chart: { type: 'column' } }],
        };

        // Get stress test chart images from the slideImages
        const stressTestImages = Object.values(this.checkupStore.slideImages['Stress Test'] || {});
        const otherImages = this.getOtherImages(stressTestImages, stressTestArea);

        if (otherImages.length > 0) {
          // Add the chart image
          const locationType = false;
          this.addImagesColumn(slide, otherImages, locationType);
          this.y += 3.5; // Adjust y position after chart
        }
      }

      // Create headers dynamically based on existing portfolios
      const headers = ['Scenario'];
      if (this.checkupStore.portfolio) {
        const portfolioName = this.checkupStore.portfolio.name || 'Portfolio';
        headers.push(portfolioName.length > 50 ? portfolioName.substring(0, 47) + '...' : portfolioName);
      }
      if (this.checkupStore.benchmark) {
        const benchmarkName = this.checkupStore.benchmark.name || 'Benchmark';
        headers.push(benchmarkName.length > 50 ? benchmarkName.substring(0, 47) + '...' : benchmarkName);
      }
      if (this.checkupStore.optimizedPortfolio) {
        const optimizedName = this.checkupStore.optimizedPortfolio.name || 'Proposed';
        headers.push(optimizedName.length > 50 ? optimizedName.substring(0, 47) + '...' : optimizedName);
      }

      // Create rows dynamically based on existing portfolios
      const rows = [
        headers,
        ...this.checkupStore.stressTestResults.map(result => {
          const row = [result.scenario];
          if (this.checkupStore.portfolio) {
            row.push(result.portfolio_impact);
          }
          if (this.checkupStore.benchmark) {
            row.push(result.benchmark_impact);
          }
          if (this.checkupStore.optimizedPortfolio) {
            row.push(result.optimized_impact);
          }
          return row;
        }),
      ];

      // Define table options
      const tableOptions = {
        x: MARGINS.left,
        y: this.y,
        w: SLIDE_WIDTH - MARGINS.left - MARGINS.right,
        fontSize: 10,
        border: { pt: '1', color: '808080' },
      };

      // Add fill and bold to the top row
      if (rows?.length) {
        const formattedRows = rows.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const cellOptions = {};

            // Top row (header row)
            if (rowIndex === 0) {
              cellOptions.bold = true;
              cellOptions.fill = 'F3F4F6'; // Add fill to header row
            }

            // First column
            if (colIndex === 0) {
              cellOptions.bold = true; // Bold first column
            }

            return {
              text: cell,
              options: cellOptions,
            };
          }),
        );

        slide.addTable(formattedRows, tableOptions);
      }

      // Dynamic methodology with rebalance frequency
      this.y += 2.75;
      this.addTextLine(slide, 'Methodology', { fontSize: 10, bold: true, w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });
      const shortMethodologyText = this.checkupStore.methodologyData['StressTestMethodology'].short;
      const cleanMethodology = this.cleanTextContent(shortMethodologyText);
      this.addTextBlock(slide, cleanMethodology, { fontSize: 8, valign: 'top', w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });
    },
    // Handles splitting into multiple pages when table is too large (by rows or columns)
    addPieChartSection(pres, area, slideImages) {
      const MAX_ROWS_FIRST_PAGE = 10;
      const MAX_ROWS_PER_PAGE = 18;
      const MAX_ACCOUNT_COLUMNS = 5; // Max account columns per table (7 total with color + category)

      const allSeries = area.chartData[0].series;

      // Skip pie chart images when there are 4+ accounts — they overlap and become unreadable
      const effectiveSlideImages = allSeries.length >= 4 ? [] : slideImages;

      if (allSeries.length <= MAX_ACCOUNT_COLUMNS) {
        // Few enough accounts to fit in a single table — split by rows if needed
        const legend = this.getPieChartLegend(area);
        const paginatedRows = this.paginateLegend(legend.legendRows, MAX_ROWS_PER_PAGE);

        const firstPageRowCount = paginatedRows[0]?.length ?? 0;
        if (firstPageRowCount <= MAX_ROWS_FIRST_PAGE) {
          // We only need one page
          this.addChartSlide(pres, area, effectiveSlideImages, legend);
        } else {
          // Legend too large, use multi-page version.
          // First page just has the charts
          this.addChartSlide(pres, area, effectiveSlideImages);

          // Remaining pages have the legend table (no charts)
          const tableOptions = {
            ...legend.tableOptions,
            y: 3, // Start table higher since there are no charts
          };

          for (let i = 0; i < paginatedRows.length; i++) {
            this.addChartSlide(
                pres, area, [],
                {
                  ...legend,
                  legendRows: paginatedRows[i],
                  tableOptions,
                },
            );
          }
        }
      } else {
        // Too many accounts — first chunk shares the slide with the chart, rest get their own slides
        const firstChunk = allSeries.slice(0, MAX_ACCOUNT_COLUMNS);
        const firstLegend = this.getPieChartLegend(area, firstChunk);
        this.addChartSlide(pres, area, effectiveSlideImages, firstLegend);

        for (let i = MAX_ACCOUNT_COLUMNS; i < allSeries.length; i += MAX_ACCOUNT_COLUMNS) {
          const seriesChunk = allSeries.slice(i, i + MAX_ACCOUNT_COLUMNS);
          const legend = this.getPieChartLegend(area, seriesChunk);
          const paginatedRows = this.paginateLegend(legend.legendRows, MAX_ROWS_PER_PAGE);
          const tableOptions = { ...legend.tableOptions, y: 3 };

          for (const rows of paginatedRows) {
            this.addChartSlide(
                pres, area, [],
                {
                  ...legend,
                  legendRows: rows,
                  tableOptions,
                },
            );
          }
        }
      }
    },
    paginateLegend(rows, pageSize) {
      const pages = [];
      for (let i = 0; i < rows.length; i += pageSize) {
        pages.push(rows.slice(i, i + pageSize));
      }
      return pages;
    },
    addChartSlide(pres, area, slideImages, legend=null) {
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#40B6D8';

      let title = area.name.replace('Breakdown', '').trim();
      if (title === 'Account') {
        title = 'Asset Class (By Account)';
      }
      if (title === 'Fees') {
        this.addFeesTitle(slide, title, area);
      } else {
        this.addSlideTitle(slide, title, { color: mainColor });
      }

      // Add svg stripe graphic
      slide.addImage({
        path: `${this.$root.$data.STATIC_ROOT}sillyimages/advisorcore_stripes.svg`,
        x: MARGINS.left + 0.11,
        y: this.y - 0.13,
        h: 0.1,
      });

      if (area.headerText) {
        this.addTextBlock(slide, area.headerText, { fontSize: 10, valign: 'top' });
      }
      if (area.explanation) {
        const explanationText = this.htmlToPlainText(area.explanation);
        slide.addNotes(explanationText);
      }

      this.addSlideFooter(slide);

      const chartType = area.chartData?.[0].chart?.type;
      if (chartType === 'pie') {
        const series = area.chartData[0].series;
        if (series && series.length > 1 && slideImages.length > 0) {
          slideImages[0].labels = series.map(s => {
            const name = s.name || '';
            return name.length > 100 ? name.substring(0, 100) + '...' : name;
          });
        }
      }
      const locationType = false;

      if (slideImages.length > 0) {
        this.addImagesColumn(slide, slideImages, locationType);
      }

      if (chartType === 'pie') {
        this.addPieChartLegend(slide, legend);
      } else if (title === 'Fees') {
        this.addFeeTableLegend(slide, area);
      } else if (title === 'Risk/Return') {
        this.addRiskReturnLegend(slide, area);
      } else if (title === 'Risk') {
        this.addRiskLegend(slide, area);
      } else if (['Market Cap', 'Equity Sector', 'Equity Geography', 'Fixed Income Type', 'Fixed Income Maturity', 'Trailing Volatility', 'Returns'].includes(title)) {
        this.addBarLineChartLegend(slide, area);
      } else if (title === 'Statistics') {
        this.addStatisticsTableLegend(slide, area);
      }
      let startingBottomTextY = SLIDE_HEIGHT - 1.25;
      if (title === 'Statistics') {
        startingBottomTextY = SLIDE_HEIGHT - 1.75;
      } else if (title === 'Equity Sector') {
        startingBottomTextY = SLIDE_HEIGHT - 0.75;
      } else if (title === 'Returns' || title === 'Risk/Return') {
        startingBottomTextY = SLIDE_HEIGHT - 1.75;
      } else if (title === 'Risk' || title === 'Trailing Volatility') {
        startingBottomTextY = SLIDE_HEIGHT - 1.35;
      }

      // const middleMargin = 0.5;

      // const endingLeftColumnX = (SLIDE_WIDTH / 2) - middleMargin;
      // const startingRightColumnX = (SLIDE_WIDTH / 2) + middleMargin;
      // const leftColumnWidth = endingLeftColumnX - MARGINS.left;
      // const rightColumnWidth = leftColumnWidth;


      this.y = startingBottomTextY;
      // right hand side text block
      // header
      this.addTextLine(slide, 'Methodology', { fontSize: 10, bold: true, w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });
      const shortMethodologyText = this.checkupStore.methodologyData[area.methodology].short;
      const cleanMethodology = this.cleanTextContent(shortMethodologyText);
      this.addTextBlock(slide, cleanMethodology, { fontSize: 8, valign: 'top', w: SLIDE_WIDTH - MARGINS.left - MARGINS.right });
    },
    addPrinciplesSlide(pres, area) {
      if (!area.principles || area.principles.length === 0) {
        return; // Don't add the slide if there are no principles
      }

      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      const title = 'Asset Location Principles';
      this.addSlideTitle(slide, title);
      this.addStripeGraphic(slide);
      this.y += 0.1;

      // Status color mapping
      const statusColors = {
        'on target': '#C8E6C9', // Green for "On Target"
        'needs review': '#FCE4A7', // Yellow for "Needs Review"
      };

      const headerRow = [
        { text: 'Current State', options: { align: 'center', valign: 'center', fontSize: 12 } },
        { text: 'Principle', options: { valign: 'center', fontSize: 12 } },
        { text: 'Reason', options: { valign: 'center', fontSize: 12 } },
        { text: 'Recommended State', options: { valign: 'center', fontSize: 12 } },
        { text: 'Status', options: { valign: 'center', fontSize: 12 } },
      ];

      const rows = area.principles.map(principle => [
        { text: principle.current_state || 'N/A', options: { valign: 'top', fontSize: 8 } },
        { text: principle.principle || 'N/A', options: { valign: 'top', fontSize: 8 } },
        { text: principle.reason || 'N/A', options: { valign: 'top', fontSize: 8 } },
        { text: principle.recommended_state || 'N/A', options: { valign: 'top', fontSize: 8 } },
        {
          text: principle.status || 'N/A',
          options: {
            valign: 'top',
            align: 'center',
            fontSize: 8,
            fill: statusColors[principle.status?.toLowerCase()] || 'transparent', // Apply the color or default to transparent
          },
        },
      ]);

      const tableData = [headerRow, ...rows];

      const tableOptions = {
        x: 0.5,
        y: this.y + 1,
        w: '90%',
        border: { pt: 1, color: '000000' },
        fontSize: 10, // Global font size for the table
        align: 'left',
      };

      slide.addTable(tableData, tableOptions);
    },
    addStatisticsTableLegend(slide, area) {
      // Extract unique portfolio names and labels
      const uniquePortfolioNames = [...new Set(area.extraData.map(data => data.name))];
      const hiddenLabels = area.visibleLabels ?
        [...new Set(area.extraData.map(data => data.label))].filter(label => !area.visibleLabels.includes(label)) :
        [];
      const hiddenSections = area.hiddenSections || [];

      // Define groups for better organization
      const groups = [
        {
          id: 'overview',
          title: 'Portfolio Overview',
          labels: [
            'Turnover',
            'Expense Ratio',
            'Yield',
            'Equity Dividend Yield',
            'Fixed Income YTM',
            'Debt to Equity',
            'Forward PE Ratio',
            'Avg # Hldgs (funds)',
          ],
        },
        {
          id: 'performance',
          title: 'Performance Metrics',
          labels: [
            '3 Yr Mean Returns',
            '3 Yr Standard Deviation',
            '5 Yr Mean Returns',
            '5 Yr Standard Deviation',
            '10 Yr Mean Returns',
            '10 Yr Standard Deviation',
          ],
        },
      ];

      // Define common styles
      const styles = {
        header: {
          align: 'center',
          valign: 'center',
          bold: true,
          color: '000000',
          fill: 'F3F4F6',
          border: { pt: '1', color: '000000' },
        },
        groupHeader: {
          valign: 'center',
          bold: true,
          color: '000000',
          fill: 'E5E7EB',
          border: { pt: '1', color: '000000' },
          align: 'left',
        },
        firstColumn: {
          align: 'left',
          valign: 'center',
          border: { pt: '1', color: '000000' },
        },
        cell: {
          align: 'center',
          border: { pt: '1', color: '000000' },
        },
      };

      // Define headers with truncation at 40 characters
      const headers = [
        { text: '', options: { ...styles.header, align: 'left' } },
        ...uniquePortfolioNames.map(header => ({
          text: header.length > 40 ? header.slice(0, 37) + '...' : header,
          options: styles.header,
        })),
      ];

      // Generate rows with grouping
      const rows = [];

      groups.forEach(group => {
        if (!hiddenSections.includes(group.id)) {
          const visibleSectionLabels = group.labels.filter(label => !hiddenLabels.includes(label));
          if (visibleSectionLabels.length > 0) {
            // Add group header
            rows.push([
              {
                text: group.title,
                options: {
                  ...styles.groupHeader,
                  colspan: headers.length,
                },
              },
            ]);

            // Add rows for visible labels
            visibleSectionLabels.forEach(label => {
              const row = [
                {
                  text: label,
                  options: styles.firstColumn,
                },
                ...uniquePortfolioNames.map(portfolioName => ({
                  text: area.extraData.find(
                      item => item.name === portfolioName && item.label === label,
                  )?.value || '--',
                  options: styles.cell,
                })),
              ];
              rows.push(row);
            });
          }
        }
      });

      // Define table layout options
      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const colWidth = tableWidth / headers.length;
      const tableOptions = {
        y: 4,
        w: tableWidth,
        fontSize: 10,
        border: { pt: '1', color: '000000' },
        colW: Array(headers.length).fill(colWidth),
        valign: 'middle',
      };

      // Add table to the slide
      slide.addTable([headers, ...rows], tableOptions);
    },
    addFeeTableLegend(slide, area) {
      if (!false /* tiered fees are vue3-only; export uses the legacy fee format on vue2 */) {
        const headers = [
          { text: '', options: { valign: 'center', bold: true, border: { pt: '1', color: '000000' } } }, // Bold the first column header
          ...[...new Set(area.extraData.map(data =>
            data.name
                .replace('Advisory and Mgmt Fees', '')
                .replace('Avg Expense Ratio', '')
                .trim(),
          ))].map(header => ({
            text: header.length > 100 ? header.substring(0, 100) + '...' : header,
            options: {
              align: 'center',
              valign: 'center',
              bold: true,
              fill: 'F3F4F6',
              border: { pt: '1', color: '000000' }, // Add border to header cells
            },
          })),
        ];

        // Generate rows for "Avg Expense Ratio" and "Advisory and Mgmt Fees"
        const rows = ['Avg Expense Ratio', 'Advisory and Mgmt Fees'].map(category => {
          return [
            { text: category, options: { valign: 'center', bold: true, border: { pt: '1', color: '000000' } } }, // Bold the row header with border
            ...[...new Set(area.extraData.map(data =>
              data.name
                  .replace('Advisory and Mgmt Fees', '')
                  .replace('Avg Expense Ratio', '')
                  .trim(),
            ))].map(header => {
              const value = area.extraData.find(data =>
                data.name.includes(header) && data.name.includes(category),
              )?.value || '--';

              return {
                text: value,
                options: {
                  align: 'center',
                  border: { pt: '1', color: '000000' }, // Add border to table cells
                },
              };
            }),
          ];
        });

        // Define table layout options
        const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
        const colWidth = tableWidth / headers.length;
        const tableOptions = {
          y: 7.5, // Adjust as needed
          w: tableWidth,
          fontSize: 10,
          border: { pt: '1', color: '000000' }, // Add border to the entire table
          colW: Array(headers.length).fill(colWidth),
        };

        // Add table to the slide
        slide.addTable([headers, ...rows], tableOptions);
        return;
      }

      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;

      // Render fee schedule tier table if a schedule with tiers or flat fee is present
      const tiers = area.feeSchedule?.tiers || [];
      const flatDollarFee = area.flatDollarFee || 0;
      if (tiers.length > 0 || flatDollarFee > 0) {
        const scheduleHeader = [
          { text: 'Fee Schedule', options: { bold: true, fill: 'F3F4F6', border: { pt: '1', color: '000000' } } },
          { text: 'Rate', options: { bold: true, align: 'center', fill: 'F3F4F6', border: { pt: '1', color: '000000' } } },
        ];
        const tierRows = [];
        let prevUpTo = 0;
        tiers.forEach((tier, i) => {
          let label;
          if (tier.upTo === null) {
            label = prevUpTo > 0 ? `Above ${this.formatCurrency(prevUpTo)}` : 'All Assets';
          } else if (i === 0) {
            label = `First ${this.formatCurrency(tier.upTo)}`;
          } else {
            label = `${this.formatCurrency(prevUpTo)} – ${this.formatCurrency(tier.upTo)}`;
          }
          tierRows.push([
            { text: label, options: { valign: 'center', border: { pt: '1', color: '000000' } } },
            { text: `${Number(tier.rate).toFixed(3)}%`, options: { align: 'center', border: { pt: '1', color: '000000' } } },
          ]);
          prevUpTo = tier.upTo || 0;
        });
        if (flatDollarFee > 0) {
          tierRows.push([
            { text: 'Annual Flat Fee', options: { valign: 'center', border: { pt: '1', color: '000000' } } },
            { text: flatDollarFee.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }), options: { align: 'center', border: { pt: '1', color: '000000' } } },
          ]);
        }
        slide.addTable([scheduleHeader, ...tierRows], {
          x: MARGINS.left,
          y: 6.3,
          w: tableWidth * 0.35,
          fontSize: 9,
          border: { pt: '1', color: '000000' },
        });
      }

      // Extract portfolio names by stripping all known row-category suffixes
      const rowSuffixList = ['Avg Expense Ratio', 'Advisory Fee', 'Management Fee', 'Annual Flat Fee', 'Total Fees'];
      const stripCategorySuffix = name => {
        for (const suffix of rowSuffixList) {
          if (name.endsWith(suffix)) return name.slice(0, name.length - suffix.length).trim();
        }
        return name.trim();
      };

      const portfolioNames = [...new Set(
          area.extraData
              .map(d => stripCategorySuffix(d.name))
              .filter(n => n.length > 0),
      )];

      // Determine which row categories to show
      const hasAnnualFlatFee = area.extraData?.some(d => d.name.includes('Annual Flat Fee'));
      const hasTotalFees = area.extraData?.some(d => d.name.endsWith('Total Fees'));
      const rowDefs = [
        { label: 'Avg Expense Ratio', key: 'avg' },
        // Match the pre-feature label ("and") when the flag is off.
        { label: false /* tiered fees are vue3-only; export uses the legacy fee format on vue2 */ ? 'Advisory & Mgmt Fees' : 'Advisory and Mgmt Fees', key: 'advMgmt' },
      ];
      if (hasAnnualFlatFee) rowDefs.push({ label: 'Annual Flat Fee', key: 'flat' });
      if (hasTotalFees) rowDefs.push({ label: 'Total Fees', key: 'total' });

      const getCellValue = (portfolioName, key) => {
        if (key === 'advMgmt') {
          const adv = area.extraData.find(d => d.name === `${portfolioName} Advisory Fee`)?.value;
          const mgmt = area.extraData.find(d => d.name === `${portfolioName} Management Fee`)?.value;
          const advVal = adv && adv !== '--' ? parseFloat(adv) : 0;
          const mgmtVal = mgmt && mgmt !== '--' ? parseFloat(mgmt) : 0;
          if (advVal === 0 && mgmtVal === 0) return '--';
          return `${(advVal + mgmtVal).toFixed(2)}%`;
        }
        const suffixMap = { avg: 'Avg Expense Ratio', flat: 'Annual Flat Fee', total: 'Total Fees' };
        return area.extraData.find(d => d.name === `${portfolioName} ${suffixMap[key]}`)?.value || '--';
      };

      const headers = [
        { text: '', options: { valign: 'center', bold: true, border: { pt: '1', color: '000000' } } },
        ...portfolioNames.map(name => ({
          text: name.length > 100 ? name.substring(0, 100) + '...' : name,
          options: {
            align: 'center',
            valign: 'center',
            bold: true,
            fill: 'F3F4F6',
            border: { pt: '1', color: '000000' },
          },
        })),
      ];

      const rows = rowDefs.map(({ label, key }) => [
        { text: label, options: { valign: 'center', bold: true, border: { pt: '1', color: '000000' } } },
        ...portfolioNames.map(portfolioName => ({
          text: getCellValue(portfolioName, key),
          options: { align: 'center', border: { pt: '1', color: '000000' } },
        })),
      ]);

      const colWidth = tableWidth / headers.length;
      const tableOptions = {
        y: 7.5,
        w: tableWidth,
        fontSize: 10,
        border: { pt: '1', color: '000000' },
        colW: Array(headers.length).fill(colWidth),
      };

      slide.addTable([headers, ...rows], tableOptions);
    },
    addRiskReturnLegend(slide, area) {
      const chartData = area.chartData?.[0];

      // Extract portfolio names and diamond marker data points
      const portfolios = chartData.series
          .filter(ds => ds.marker?.symbol === 'diamond') // Filter series with diamond markers
          .map(ds => {
            const diamondPoint = ds.data[0]; // Assuming one data point per series for diamonds
            return {
              name: ds.name, // Portfolio name
              rtns: diamondPoint?.y || 0, // 3 Year Returns (y-axis value)
              stdev: diamondPoint?.x || 0, // 3 Year Standard Deviation (x-axis value)
            };
          });

      // Construct table rows
      const headers = [
        { text: 'Metric', options: { bold: true, fill: 'F3F4F6', align: 'left', valign: 'middle' } },
        ...portfolios.map(portfolio => ({
          text: portfolio.name.length > 100 ? portfolio.name.substring(0, 100) + '...' : portfolio.name,
          options: { bold: true, fill: 'F3F4F6', align: 'center', valign: 'middle' },
        })),
      ];

      const rows = [
        [
          { text: '3 Year Returns', options: { bold: true, align: 'left', valign: 'middle' } },
          ...portfolios.map(portfolio => ({
            text: this.normalizePercentage(portfolio.rtns), // Format as percentage
            options: { align: 'center', valign: 'middle' },
          })),
        ],
        [
          { text: '3 Year Volatility', options: { bold: true, align: 'left', valign: 'middle' } },
          ...portfolios.map(portfolio => ({
            text: this.normalizePercentage(portfolio.stdev), // Format as percentage
            options: { align: 'center', valign: 'middle' },
          })),
        ],
      ];

      const tableData = [headers, ...rows];

      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const colCount = headers.length;
      const colWidth = tableWidth / colCount;

      slide.addTable(tableData, {
        x: MARGINS.left,
        y: 7,
        w: tableWidth,
        rowH: 0.3,
        fontSize: 10,
        colW: Array(colCount).fill(colWidth),
        border: { pt: '1', color: '000000' },
        valign: 'middle',
      });
    },
    addRiskLegend(slide, area) {
      const truncate = (text, maxLength) => {
        return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
      };

      // Define the examples array directly within the function
      const examples = [
        { type: 'Cash', score: 1 },
        { type: 'Treasury Bond', score: 10 },
        { type: '40/60 Stock/Bond', score: 30 },
        { type: '60/40 Stock/Bond', score: 50 },
        { type: '80/20 Stock/Bond', score: 70 },
        { type: 'Global Stock Index', score: 90 },
        { type: 'Penny Stock', score: 99 },
      ];

      // Combine portfolios from area and examples, then sort by risk score
      const portfolios = area.chartData[0].datasets
          .filter(dataset => dataset.data[0] !== null && dataset.data[0] !== undefined) // Ignore datasets without a valid risk score
          .map(dataset => ({
            name: dataset.label,
            riskScore: dataset.data[0].toFixed(0),
            isPortfolio: true,
          }));

      const examplesData = examples.map(example => ({
        name: example.type,
        riskScore: example.score,
        isPortfolio: false,
      }));

      const sortedData = [...portfolios, ...examplesData].sort((a, b) => a.riskScore - b.riskScore);

      // Headers for the table
      const headers = [
        {
          text: 'Your Portfolios in Context',
          options: { bold: true, fill: 'F3F4F6', align: 'left', valign: 'middle' },
        },
        {
          text: 'Risk Score',
          options: { bold: true, fill: 'F3F4F6', align: 'center', valign: 'middle' },
        },
      ];

      // Rows for the table
      const rows = sortedData.map(entry => {
        return [
          {
            text: truncate(entry.name, 46),
            options: { bold: entry.isPortfolio, align: 'left', valign: 'middle' },
          },
          {
            text: entry.riskScore.toString(),
            options: { bold: entry.isPortfolio, align: 'center', valign: 'middle' },
          },
        ];
      });

      const tableData = [headers, ...rows];

      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const colCount = headers.length;
      const colWidth = tableWidth / colCount;

      slide.addTable(tableData, {
        x: MARGINS.left,
        y: 6, // Adjust y position as needed
        w: tableWidth,
        rowH: 0.3,
        fontSize: 10,
        colW: Array(colCount).fill(colWidth),
        border: { pt: '1', color: '000000' },
        valign: 'middle',
      });
    },
    addBarLineChartLegend(slide, namedArea) {
      let area;
      let labels; let datasets;

      if (namedArea.name === 'Returns') {
        area = this.checkupStore.areas.find(a => a.name === 'Trailing Returns');
        const chartData = area.chartData?.[0];
        labels = chartData.xAxis.categories;
        datasets = chartData.series.map(ds => ({
          ...ds,
          label: ds.name, // Rename 'name' to 'label' for uniform handling
          data: ds.data.map(value => value / 100), // we multiply by 100 later in the code
        }));
      } else {
        area = namedArea;
        const chartData = area.chartData?.[0];
        ({ labels, datasets } = chartData); // Destructure labels and datasets
      }
      const truncate = (text, maxLength) => {
        return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
      };
      const headers = [
        {
          text: area.name.replace('Breakdown', '').trim(),
          options: { bold: true, fill: 'F3F4F6', align: 'left', valign: 'middle' },
        },
        ...datasets.map(ds => {
          return {
            text: truncate(ds.label, 30),
            options: { bold: true, fill: 'F3F4F6', align: 'center', valign: 'middle' },
          };
        }),
      ];
      const rows = labels.map((label, idx) => {
        return [
          {
            text: label,
            options: { bold: true, align: 'left', valign: 'middle' }, // Make the first column bold
          },
          ...datasets.map(ds => {
            const val = this.normalizePercentage(ds.data[idx] * 100);
            return { text: val, options: { align: 'center', valign: 'middle' } };
          }),
        ];
      });

      const tableData = [headers, ...rows];

      // Table layout options
      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const colCount = headers.length;
      const colWidth = tableWidth / colCount;

      // Equity sector and fixed income maturity have a lot of rows so need smaller fonts.
      if (namedArea.name === 'Equity Sector Breakdown' || namedArea.name === 'Fixed Income Maturity Breakdown') {
        slide.addTable(tableData, {
          x: MARGINS.left,
          y: 7,
          w: tableWidth,
          rowH: 0.18,
          fontSize: 7,
          colW: Array(colCount).fill(colWidth),
          border: { pt: '1', color: '000000' },
          valign: 'middle',
        });
      } else {
        slide.addTable(tableData, {
          x: MARGINS.left,
          y: 7,
          w: tableWidth,
          rowH: 0.3,
          fontSize: 10,
          colW: Array(colCount).fill(colWidth),
          border: { pt: '1', color: '000000' },
          valign: 'middle',
        });
      }
    },
    getPieChartLegend(area, seriesSubset = null) {
      const dataSeries = seriesSubset || area.chartData[0].series;

      const headerRow = [
        {
          text: '',
          options: {
            align: 'center',
            valign: 'center',
            fill: { color: 'F3F4F6' },
            bold: true,
            border: { pt: '1', color: '000000' },
          },
        },
        {
          text: 'Category',
          options: {
            valign: 'center',
            fill: { color: 'F3F4F6' },
            bold: true,
            border: { pt: '1', color: '000000' },
          },
        },
        ...dataSeries.map(series => {
          const clippedName = series.name.length > 50 ? series.name.substring(0, 50).trim() + '...' : series.name;
          return {
            text: clippedName,
            options: {
              align: 'center',
              valign: 'center',
              fill: { color: 'F3F4F6' },
              bold: true,
              border: { pt: '1', color: '000000' },
            },
          };
        }),
      ];

      // Account Value row for Account Breakdown area
      const accountValueRow = area.name === 'Account Breakdown' ? (() => {
        const accountValues = this.getAccountValues();
        return [[
          {
            text: '',
            options: {
              fill: { color: 'F3F4F6' },
              border: { pt: '1', color: '000000' },
            },
          },
          {
            text: 'Account Value',
            options: {
              align: 'left',
              bold: true,
              fill: { color: 'F3F4F6' },
              border: { pt: '1', color: '000000' },
            },
          },
          ...dataSeries.map(series => ({
            text: this.formatCurrency(accountValues[series.name] || 0),
            options: {
              align: 'center',
              bold: true,
              fill: { color: 'F3F4F6' },
              border: { pt: '1', color: '000000' },
            },
          })),
        ]];
      })() : [];

      // Legend rows with color fill for the first column and bold for the first column text
      const legendColumns = this.chartSections(area).filter(col => col !== undefined && col !== null);

      const legendRows = legendColumns.map(col => {
        const color = this.getColorForChartSection(area, col);
        return [
          {
            text: '',
            options: {
              fill: { color },
              border: { pt: '1', color: '000000' },
            },
          },
          {
            text: col,
            options: {
              align: 'left',
              bold: true,
              border: { pt: '1', color: '000000' },
            },
          },
          ...dataSeries.map(series => ({
            text: this.getPercentageForChartSection(series, col),
            options: {
              align: 'center',
              border: { pt: '1', color: '000000' },
            },
          })),
        ];
      });

      // Table options, including column widths
      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const widths = { color: 0.25, category: 1.5 };
      const remainingWidth = tableWidth - widths.color - widths.category;
      const tableOptions = {
        y: 6,
        w: tableWidth,
        fontSize: 10,
        colW: [
          widths.color,
          widths.category,
          ...Array(dataSeries.length).fill(remainingWidth / dataSeries.length),
        ],
        border: { pt: '1', color: '000000' }, // Border for the entire table
        valign: 'middle',
      };

      return { headerRow, legendRows: [...accountValueRow, ...legendRows], tableOptions };
    },
    addPieChartLegend(slide, legend) {
      // Add the table if there are rows
      if (legend?.legendRows.length > 0) {
        slide.addTable([legend.headerRow, ...legend.legendRows], legend.tableOptions);
      }
    },
    getPercentageForChartSection(series, chartSection) {
      const dataPoint = series.data.find(point => point.name === chartSection);
      return dataPoint ? this.normalizePercentage(dataPoint.y * 100) : '0%';
    },
    getColorForChartSection(area, chartSection) {
      let color = '';
      if (area && area.chartData && area.chartData[0] && area.chartData[0].series) {
        area.chartData[0].series.forEach(series => {
          const dataPoint = series.data.find(point => point.name === chartSection);
          if (dataPoint) {
            color = dataPoint.color;
          }
        });
      }
      return color;
    },
    chartSections(area) {
      const series = area.chartData[0].series;
      if (!series) return [];

      const chartsections = new Set();
      series.forEach(series => {
        series.data.forEach(point => {
          chartsections.add(point.name);
        });
      });
      return Array.from(chartsections);
    },
    async getChartImages(charts) {
      if (!charts?.length) return [];

      const imagePromises = charts.map(async chart => {
        const base64 = chart.toBase64Image('image/png', 1);
        const aspectRatio = await this.getAspectRatio(base64);

        return {
          chartType: chart.config._config.type,
          base64String: `image/png;base64,${base64}`,
          aspectRatio,
        };
      });
      return await Promise.all(imagePromises);
    },
    async getAspectRatio(src) {
      const { naturalWidth, naturalHeight } = await this.getImageMetadata(src);
      return naturalWidth / naturalHeight;
    },
    getOtherImages(images, area) {
      if (!images?.length) return [];

      return images.map(image => ({
        ...image,
        chartType: area.chartData?.[0].chart?.type || 'other',
      }));
    },
    addImagesColumn(slide, images, locationType) {
      function getImageWidth(image, height) {
        const ratio = image.aspectRatio;
        if (ratio) return height * ratio;

        const chartType = image.chartType;
        if (['bar', 'line'].includes(chartType)) return height * 2;

        return height;
      }

      function scaleToMaxWidth(targetWidth, targetHeight, maxWidth) {
        const ratio = targetWidth / targetHeight;
        const w = Math.min(targetWidth, maxWidth);
        const h = w / ratio;
        return { w, h };
      }

      const margins = {
        ...MARGINS,
        top: 3,
        bottom: 3,
      };
      if (locationType === true) {
        margins.top = 3.5;
        margins.bottom = 2.5;
      }

      let targetHeight = ((SLIDE_HEIGHT - margins.top - margins.bottom) / images.length) - margins.image;

      const imagesWithData = images.map((image, i) => {
        if (image.chartType === 'pie') {
          targetHeight = 5.5;
        }
        const targetWidth = getImageWidth(image, targetHeight);
        const maxWidth = SLIDE_WIDTH - margins.left - margins.right;
        const { w, h } = scaleToMaxWidth(targetWidth, targetHeight, maxWidth);
        let y = margins.top + (i * (h + margins.image));
        if (image.chartType === 'pie') {
          y = y - .35;
        }

        return { ...image, w, h, y };
      });

      const widthOfWidestImage = Math.max(...imagesWithData.map(image => image.w));
      const x = (SLIDE_WIDTH - widthOfWidestImage) / 2;

      imagesWithData.forEach(image => {
        slide.addImage({ data: image.base64String, w: image.w, h: image.h, x, y: image.y });
        if (image.labels) {
          this.addSingleRowTable(
              slide,
              image.labels,
              { w: image.w, x, y: image.y + image.h - 0.5, fontSize: 8 });
        }
      });
    },
    // Adds a single row table of the provided labels array
    addSingleRowTable(slide, texts, config={}) {
      const table = [
        texts.map(text => ({ text, options: { align: 'center' } })),
      ];
      slide.addTable(table, config);
    },
    // Scale image to fit within specified box (height and width) while preserving aspect ratio
    // Returns the modified width and height to use for the image
    // If image is already smaller than the box, it will return the original dimensions.
    scaleToBox(original, box) {
      if (original.w < box.w && original.h < box.h) return original;

      // How much do we need to scale to fit vertically?
      const vScale = box.h / original.h;

      // How much do we need to scale to fit horizontally?
      const hScale = box.w / original.w;

      // Use the smaller of the two scales
      const scale = Math.min(vScale, hScale);

      return {
        w: original.w * scale,
        h: original.h * scale,
      };
    },
    pixelsToInches(dimensions) {
      return {
        w: dimensions.w / PPI,
        h: dimensions.h / PPI,
      };
    },
    addStressTestMethodologySlide(pres) {
      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      this.addSlideTitle(slide, 'Methodology');
      this.addTextLine(slide, 'Stress Testing', { fontSize: 12, bold: true });

      const methodologyText = this.checkupStore.methodologyData['StressTestMethodology'].long;
      const clean = this.cleanTextContent(methodologyText);

      // Determine font size based on text length
      let fontSize = 10;
      const len = clean.length;
      if (len > 7000) {
        fontSize = 8;
      } else if (len > 6000) {
        fontSize = 9;
      } else if (len > 5000) {
        fontSize = 9.5;
      }
      this.addTextBlock(slide, clean, { fontSize, valign: 'top' });
    },
    addMethodologySlide(pres, area) {
      const methodologyText = this.checkupStore.methodologyData[area.methodology].long;
      const clean = this.cleanTextContent(methodologyText);

      const slide = pres.addSlide();
      this.y = 0;
      this.addSlideHeader(slide, 1, 1);

      this.addSlideTitle(slide, 'Methodology');
      this.addTextLine(slide, area.name, { fontSize: 12, bold: true });

      // Determine font size based on text length
      let fontSize = 10;
      const len = clean.length;
      if (len > 7000) {
        fontSize = 8;
      } else if (len > 6000) {
        fontSize = 9;
      } else if (len > 5000) {
        fontSize = 9.5;
      }
      this.addTextBlock(slide, clean, { fontSize, valign: 'top' });
    },
    addOptimizationSlide(pres) {
      const slide = pres.addSlide();
      this.y = 0;

      // Header and title
      this.addSlideHeader(slide, 1, 1);
      const mainColor = this.advisorStore.firmAdvisorCoreSettings?.portfolio_color || '#40B6D8';
      this.addSlideTitle(slide, 'Portfolio Optimization', { color: mainColor });

      // Count how many data rows we’ll have
      const dataCount = this.checkupStore.optimization.optimizations.length;
      // If more than 1 data row, shrink by 0.5
      const baseFontSize = dataCount > 1 ? 9 - 0.5 : 9;
      const cellFontSize = dataCount > 1 ? 12 - 0.5 : 12;

      // Build the rows array (header + data rows)
      const rows = [
        [
          { text: 'Tracking Error', options: { align: 'center' } },
          { text: 'Portfolio Name' },
        ],
      ];

      this.checkupStore.optimization.optimizations.forEach(o => {
        const name = o.optimization_portfolio_name;
        const trackingError = o.diagnostics.tracking_error * 100;
        rows.push([
          {
            text: trackingError.toFixed(2) + '%',
            options: {
              align: 'center',
              valign: 'center',
              fontSize: cellFontSize,
              bold: true,
            },
          },
          { text: name.length > 100 ? name.substring(0, 100) + '...' : name },
        ]);
      });

      // Table width and options
      const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
      const tableOptions = {
        x: MARGINS.left,
        y: this.y,
        w: tableWidth,
        fontSize: baseFontSize,
        border: { pt: '1', color: '808080' },
        colW: [1.5, tableWidth - 1.5],
      };

      // Add table
      slide.addTable(rows, tableOptions);

      // Advance y based on row count
      const rowHeight = 0.38;
      this.y += rows.length * rowHeight;

      // Explanatory text
      this.addTextLine(
          slide,
          'Tracking error compares the optimized portfolio to a benchmark based on your specified risk level:',
          { fontSize: baseFontSize, align: 'center' },
      );
      if (this.checkupStore.riskBasedBenchmark?.nickname) {
        this.addTextLine(
            slide,
            this.checkupStore.riskBasedBenchmark.nickname,
            { fontSize: baseFontSize, align: 'center', bold: true },
        );
      }

      // Add methodology items, adjusting y as needed
      optimizerMethodology.forEach(item => {
        if (item.margins?.top) this.y += item.margins.top;
        const config = item.config || { fontSize: 7.5 };
        if (Array.isArray(item.text)) {
          this.addTextList(slide, item.text, config, { bullet: true });
        } else {
          this.addTextBlock(slide, item.text, config);
        }
        if (item.margins?.bottom) this.y += item.margins.bottom;
      });
    },
    lightenColor(color, percent) {
      const num = parseInt(color.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const B = (num >> 8 & 0x00FF) + amt;
      const G = (num & 0x0000FF) + amt;
      return '#' + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (B<255?B<1?0:B:255)*0x100 + (G<255?G<1?0:G:255)).toString(16).slice(1);
    },
    darkenColor(color, percent) {
      const num = parseInt(color.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) - amt;
      const B = (num >> 8 & 0x00FF) - amt;
      const G = (num & 0x0000FF) - amt;
      return '#' + (0x1000000 + (R>0?R<255?R:255:0)*0x10000 + (B>0?B<255?B:255:0)*0x100 + (G>0?G<255?G:255:0)).toString(16).slice(1);
    },
    getAcct(holding) {
      if (holding.account) {
        return holding.account;
      }
      return null;
    },
    getTickerField(holding, field) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        return holding.ticker[field];
      }
      return null;
    },
    getSymbol(holding) {
      if (typeof holding.ticker === 'object' && holding.ticker !== null) {
        if (
          holding.metadata?.uploadedsymbol &&
          holding.ticker.symbol &&
          holding.metadata?.uploadedsymbol !== holding.ticker.symbol
        ) {
          return holding.metadata.uploadedsymbol;
        }
        return holding.ticker['symbol'];
      } else if (typeof holding.ticker === 'string' && holding.found === false) {
        return holding.ticker;
      } else {
        try {
          // The else-if is for CSV uploads or MTF cases when the uploaded name is different than the symbol used (i.e., proxy ticker)
          if (holding.metadata?.uploadedsymbol === holding.ticker.symbol && holding.metadata?.notes) {
            return holding.metadata.uploadedname;
          }
          return holding.metadata?.uploadedsymbol;
        } catch (error) {
          // If there is an error, default to returning holding.ticker if it exists, or '' if it doesn't
          return holding.ticker || holding.isin || holding.cusip || '';
        }
      }
    },
    getName(holding) {
      if (holding.uploadedname) {
        return holding.uploadedname;
      }
      if (holding.metadata?.uploadedname && holding.metadata?.uploadedsymbol !== holding.ticker.symbol) {
        return holding.metadata.uploadedname;
      }
      return this.getTickerField(holding, 'full_name');
    },
    getSecurityType(holding) {
      if (holding.securitytype) return holding.securitytype;
      return this.getTickerField(holding, 'asset_type');
    },
    // Updated holdings slide function
    addHoldingsSlide(pres, holdingsType = 'portfolio') {
      // Determine which holdings to use based on type
      let holdings = [];
      let portfolioName = '';

      if (holdingsType === 'portfolio' && this.checkupStore.holdingsFromPortfolios && this.checkupStore.holdingsFromPortfolios.length > 0) {
        holdings = this.checkupStore.holdingsFromPortfolios;
        portfolioName = this.checkupStore.portfolio.name || 'Current';
      } else if (holdingsType === 'benchmark' && this.checkupStore.benchmark && this.checkupStore.benchmark.holdings && this.checkupStore.benchmark.holdings.length > 0) {
        holdings = this.checkupStore.benchmark.holdings;
        portfolioName = this.checkupStore.benchmark.name || 'Benchmark';
      } else if (holdingsType === 'proposed' && this.checkupStore.optimizedPortfolio && this.checkupStore.optimizedPortfolio.holdings && this.checkupStore.optimizedPortfolio.holdings.length > 0) {
        holdings = this.checkupStore.optimizedPortfolio.holdings;
        portfolioName = this.checkupStore.optimizedPortfolio.name || 'Proposed Portfolio';
      } else {
        // No valid holdings to display
        console.log(`No ${holdingsType} holdings to display`);
        return; // Exit the function without creating a slide
      }

      const truncatePortfolioName = (name, maxLength = 70) => {
        if (!name || name.length <= maxLength) return name;
        return name.substring(0, maxLength - 3) + '...';
      };

      const truncatedPortfolioName = truncatePortfolioName(portfolioName);
      const SLIDE_TITLE = `Holdings for ${truncatedPortfolioName}`;

      // Get slide dimensions and layout constraints
      const availableHeight = SLIDE_HEIGHT - MARGINS.top - MARGINS.bottom;
      const baseRowHeight = 0.3; // Base height per row in inches
      const headerHeight = 0.4; // Slightly increased header height
      const summaryRowHeight = 0.4; // Slightly increased height for summary rows with two lines
      const titleSpace = 0.8; // Space taken by the title
      const headerSpace = 0.7; // Space taken by the slide header
      const safetyMargin = 0.5; // Safety margin to prevent overlaps

      // Helper functions for truncating long content - consistent limits
      const truncateSymbol = (symbol, maxLength = 30) => {
        if (!symbol || symbol.length <= maxLength) return symbol;
        return symbol.substring(0, maxLength - 3) + '...';
      };

      const truncateName = (name, maxLength = 100) => {
        if (!name || name.length <= maxLength) return name;
        return name.substring(0, maxLength - 3) + '...';
      };

      // Calculate total market value
      const totalMarketValue = holdings.reduce((sum, holding) => {
        const dollarValue = typeof holding.dollars === 'string' ?
          parseFloat(holding.dollars.replace(/[$,]/g, '')) :
          holding.dollars;

        return sum + (isNaN(dollarValue) ? 0 : dollarValue);
      }, 0);

      // Process holdings data
      const processedHoldings = holdings.map(holding => {
        // Calculate dollar value
        const dollarValue = typeof holding.dollars === 'string' ?
          parseFloat(holding.dollars.replace(/[$,]/g, '')) :
          holding.dollars;

        const dollarValueNum = isNaN(dollarValue) ? 0 : dollarValue;

        // Calculate percentage as a number for sorting
        const percentageValue = totalMarketValue > 0 ?
          (dollarValueNum / totalMarketValue) :
          0;

        // Format percentage for display
        const percentageFormatted = this.toPercent(percentageValue);

        // Get security type
        const securityType = this.getSecurityType(holding);

        // Get account
        const account = this.getAcct(holding);

        return {
          holding,
          dollarValue: dollarValueNum,
          percentageValue,
          percentageFormatted,
          securityType,
          account,
        };
      });

      // Check if any holdings have non-empty account values
      const hasAccountValues = processedHoldings.some(item => {
        return item.account !== null && item.account !== undefined && item.account !== '';
      });

      // Sort the processed holdings: 1) by account (if present), 2) by security type, 3) by dollar amount (descending)
      processedHoldings.sort((a, b) => {
        // First sort by account if accounts column is shown
        if (hasAccountValues) {
          const accountA = a.account || ''; // Treat null/undefined as empty string for sorting
          const accountB = b.account || '';

          if (accountA < accountB) return -1;
          if (accountA > accountB) return 1;
        }

        // Then sort by security type
        if (a.securityType < b.securityType) return -1;
        if (a.securityType > b.securityType) return 1;

        // Finally sort by dollar value (percentage) in descending order
        return b.dollarValue - a.dollarValue;
      });

      // Get the base color from advisor settings based on holdings type
      let baseColor = '1F497D'; // Default dark blue as fallback

      if (holdingsType === 'portfolio' && this.advisorStore.firmAdvisorCoreSettings?.portfolio_color) {
        baseColor = this.advisorStore.firmAdvisorCoreSettings.portfolio_color.replace('#', '');
      } else if (holdingsType === 'benchmark' && this.advisorStore.firmAdvisorCoreSettings?.benchmark_color) {
        baseColor = this.advisorStore.firmAdvisorCoreSettings.benchmark_color.replace('#', '');
      } else if (holdingsType === 'proposed' && this.advisorStore.firmAdvisorCoreSettings?.proposed_color) {
        baseColor = this.advisorStore.firmAdvisorCoreSettings.proposed_color.replace('#', '');
      }

      // Create color variations for different elements with a clear visual hierarchy
      const COLORS = {
        header: baseColor, // Base color for main header (darkest)
        headerText: 'FFFFFF', // White text for header
        accountHeader: this.lightenColor(`#${baseColor}`, 65).replace('#', ''), // Account header (second darkest)
        groupHeader: this.lightenColor(`#${baseColor}`, 70).replace('#', ''), // Security type summary (darker than before)
        rowEven: this.lightenColor(`#${baseColor}`, 90).replace('#', ''), // Even rows (very light version of base color)
        rowOdd: 'F9F9F9', // Odd rows (light gray, not tied to base color)
      };

      // Create headers with improved styling
      const headers = [
        {
          text: hasAccountValues ? 'Account/Security Type' : 'Security Type',
          options: {
            bold: true,
            fill: COLORS.header,
            color: COLORS.headerText,
            align: 'left',
            valign: 'middle',
            fontSize: 11, // Slightly larger font for headers
          },
        },
        {
          text: 'Symbol/CUSIP',
          options: {
            bold: true,
            fill: COLORS.header,
            color: COLORS.headerText,
            align: 'left',
            valign: 'middle',
            fontSize: 11,
          },
        },
      ];

      headers.push(
          {
            text: 'Name',
            options: {
              bold: true,
              fill: COLORS.header,
              color: COLORS.headerText,
              align: 'left',
              valign: 'middle',
              fontSize: 11,
            },
          },
          {
            text: hasAccountValues ? 'Pct of Portfolio (Account)' : 'Pct of Portfolio',
            options: {
              bold: true,
              fill: COLORS.header,
              color: COLORS.headerText,
              align: 'right',
              valign: 'middle',
              fontSize: 11,
            },
          },
      );

      // Calculate account totals if needed
      const accountTotals = {};
      if (hasAccountValues) {
        processedHoldings.forEach(item => {
          const account = item.account || '';
          if (!accountTotals[account]) {
            accountTotals[account] = 0;
          }
          accountTotals[account] += item.dollarValue;
        });
      }

      // Group holdings by account and security type
      const groupedHoldings = {};

      // First level grouping - by account
      const accountGroups = {};

      if (hasAccountValues) {
        processedHoldings.forEach(item => {
          const account = item.account || '';
          if (!accountGroups[account]) {
            accountGroups[account] = {
              items: [],
              totalDollars: 0,
              totalPercentage: 0,
              account: account,
            };
          }

          accountGroups[account].items.push(item);
          accountGroups[account].totalDollars += item.dollarValue;
          accountGroups[account].totalPercentage += item.percentageValue;
        });
      }

      // Second level grouping - by security type within each account
      processedHoldings.forEach(item => {
        const accountKey = hasAccountValues ? (item.account || '') : 'all';
        const securityType = item.securityType;
        const groupKey = `${accountKey}|${securityType}`;

        if (!groupedHoldings[groupKey]) {
          groupedHoldings[groupKey] = {
            items: [],
            totalDollars: 0,
            totalPercentage: 0,
            securityType: securityType,
            account: hasAccountValues ? item.account : null,
          };
        }

        groupedHoldings[groupKey].items.push(item);
        groupedHoldings[groupKey].totalDollars += item.dollarValue;
        groupedHoldings[groupKey].totalPercentage += item.percentageValue;
      });

      // Create rows data with account summary rows and security type summary rows
      const rows = [];
      let currentAccount = null;
      let currentGroup = null;
      let rowIndex = 0;

      // Helper function to create an account summary row
      const createAccountSummaryRow = (account, group) => {
        const totalPercentFormatted = this.toPercent(group.totalPercentage);
        const rowFill = COLORS.accountHeader;

        const cellStyle = {
          align: 'left',
          valign: 'middle',
          fill: rowFill,
          bold: true, // Make summary rows bold
          fontSize: 10,
        };

        const numColumns = headers.length;
        const rowData = Array(numColumns).fill().map(() => ({
          text: '',
          options: { ...cellStyle },
        }));

        // Set the first cell to display the account name
        // Truncate long account names - consistent threshold
        const displayAccount = account.length > 50 ? account.substring(0, 47) + '...' : account;
        rowData[0].text = `Account: ${displayAccount || 'Unspecified'}`;
        rowData[0].options.fontSize = 10;

        // Set the percentage cell
        const percentColIndex = 3;
        rowData[percentColIndex].text = totalPercentFormatted;
        rowData[percentColIndex].options.align = 'right';

        return {
          data: rowData,
          height: summaryRowHeight + 0.05, // Slightly taller for account summary
          isAccountSummary: true,
        };
      };

      // Helper function to create a security type summary row
      const createSecurityTypeSummaryRow = group => {
        const totalPercentFormatted = this.toPercent(group.totalPercentage);
        const totalPercentOfAccount = hasAccountValues && group.account ?
          (group.totalDollars / (accountTotals[group.account || ''] || 1)) : null;

        const totalPercentOfAccountFormatted = totalPercentOfAccount ?
          this.toPercent(totalPercentOfAccount) : '';

        const rowFill = COLORS.groupHeader;

        const cellStyle = {
          align: 'left',
          valign: 'middle',
          fill: rowFill,
          bold: true, // Make summary rows bold
          fontSize: 10,
        };

        const numColumns = headers.length;
        const rowData = Array(numColumns).fill().map(() => ({
          text: '',
          options: { ...cellStyle },
        }));

        // Set the security type cell - include account name on a separate line when available
        if (hasAccountValues && group.account) {
          // Truncate long account names - consistent threshold
          const displayAccount = group.account.length > 40 ?
            group.account.substring(0, 37) + '...' : group.account;
          rowData[0].text = `${group.securityType}\n(${displayAccount})`;
        } else {
          rowData[0].text = group.securityType;
        }

        // Set the percentage cell
        const percentColIndex = 3;
        if (hasAccountValues && totalPercentOfAccount) {
          rowData[percentColIndex].text = `${totalPercentFormatted} (${totalPercentOfAccountFormatted})`;
        } else {
          rowData[percentColIndex].text = totalPercentFormatted;
        }
        rowData[percentColIndex].options.align = 'right';

        return {
          data: rowData,
          height: summaryRowHeight,
          isSecurityTypeSummary: true,
        };
      };

      // Add rows with account headers and security type headers
      let proxyAsterisk = false;

      // If we have accounts, first organize by account
      if (hasAccountValues) {
        // Sort accounts for consistent display
        const sortedAccounts = Object.keys(accountGroups).sort();

        sortedAccounts.forEach(accountName => {
          const accountGroup = accountGroups[accountName];

          // Add the account summary row
          if (currentAccount !== accountName) {
            rows.push(createAccountSummaryRow(accountName, accountGroup));
            currentAccount = accountName;
          }

          // Now add all security types within this account
          const securityTypesInAccount = {};
          accountGroup.items.forEach(item => {
            if (!securityTypesInAccount[item.securityType]) {
              securityTypesInAccount[item.securityType] = true;
            }
          });

          // Sort security types for consistent display
          const sortedSecurityTypes = Object.keys(securityTypesInAccount).sort();

          sortedSecurityTypes.forEach(securityType => {
            const groupKey = `${accountName}|${securityType}`;
            const group = groupedHoldings[groupKey];

            if (group) {
              // Add the security type summary row
              if (currentGroup !== groupKey) {
                rows.push(createSecurityTypeSummaryRow(group));
                currentGroup = groupKey;
              }

              // Add all holdings in this security type
              group.items.sort((a, b) => b.dollarValue - a.dollarValue).forEach(item => {
                // Add the regular holding row
                const rowFill = rowIndex % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd;
                rowIndex++;

                const cellStyle = {
                  align: 'left',
                  valign: 'middle',
                  fill: rowFill,
                  fontSize: 10,
                };

                const holding = item.holding;
                const rowData = [
                  { text: '', options: cellStyle }, // Empty text for security type on regular rows
                ];

                // Handle symbol/CUSIP with truncation if needed
                let symbolText = this.getSymbol(holding);

                // Special case for proxy ticker
                if (
                  holding.metadata?.uploadedsymbol &&
                  holding.ticker?.symbol &&
                  holding.metadata.uploadedsymbol !== holding.ticker.symbol
                ) {
                  proxyAsterisk = true;
                  symbolText += '*';
                }

                // Truncate for very long symbols
                symbolText = truncateSymbol(symbolText);
                rowData.push({ text: symbolText, options: cellStyle });

                // Get and truncate name if needed
                const truncatedName = truncateName(this.getName(holding));

                // Calculate percentage of account
                const percentOfAccount = accountTotals[accountName] > 0 ?
                  (item.dollarValue / accountTotals[accountName]) : 0;
                const percentOfAccountFormatted = this.toPercent(percentOfAccount);

                rowData.push(
                    { text: truncatedName, options: cellStyle },
                    {
                      text: hasAccountValues ?
                      `${item.percentageFormatted} (${percentOfAccountFormatted})` :
                      item.percentageFormatted,
                      options: {
                        ...cellStyle,
                        align: 'right',
                      },
                    },
                );

                // Estimate row height based on the truncated name length
                // (not the original name length, since that's not what's displayed)
                let estimatedHeight = baseRowHeight;

                if (truncatedName && truncatedName.length > 50) {
                  estimatedHeight = baseRowHeight * 1.5; // 50% taller for long names
                }
                if (truncatedName && truncatedName.length > 90) {
                  estimatedHeight = baseRowHeight * 2; // Twice as tall for very long names
                }

                rows.push({
                  data: rowData,
                  height: estimatedHeight,
                  percentageValue: item.percentageValue,
                  securityType: item.securityType,
                  account: item.account,
                });
              });
            }
          });
        });
      } else {
        // No accounts - just organize by security type
        // Sort security types for consistent display
        const securityTypes = [...new Set(processedHoldings.map(item => item.securityType))].sort();

        securityTypes.forEach(securityType => {
          const groupKey = `all|${securityType}`;
          const group = groupedHoldings[groupKey];

          if (group) {
            // Add the security type summary row
            if (currentGroup !== groupKey) {
              rows.push(createSecurityTypeSummaryRow(group));
              currentGroup = groupKey;
            }

            // Add all holdings in this security type
            group.items.sort((a, b) => b.dollarValue - a.dollarValue).forEach(item => {
              // Add the regular holding row
              const rowFill = rowIndex % 2 === 0 ? COLORS.rowEven : COLORS.rowOdd;
              rowIndex++;

              const cellStyle = {
                align: 'left',
                valign: 'middle',
                fill: rowFill,
                fontSize: 10,
              };

              const holding = item.holding;
              const rowData = [
                { text: '', options: cellStyle }, // Empty text for security type on regular rows
              ];

              // Handle symbol/CUSIP with truncation if needed
              let symbolText = this.getSymbol(holding);

              // Special case for proxy ticker
              if (
                holding.metadata?.uploadedsymbol &&
                holding.ticker?.symbol &&
                holding.metadata.uploadedsymbol !== holding.ticker.symbol
              ) {
                proxyAsterisk = true;
                symbolText += '*';
              }

              // Truncate for very long symbols
              symbolText = truncateSymbol(symbolText);
              rowData.push({ text: symbolText, options: cellStyle });

              // Get and truncate name if needed
              const truncatedName = truncateName(this.getName(holding));

              rowData.push(
                  { text: truncatedName, options: cellStyle },
                  {
                    text: item.percentageFormatted,
                    options: {
                      ...cellStyle,
                      align: 'right',
                    },
                  },
              );

              // Estimate row height based on the truncated name length
              let estimatedHeight = baseRowHeight;

              if (truncatedName && truncatedName.length > 50) {
                estimatedHeight = baseRowHeight * 1.5; // 50% taller for long names
              }
              if (truncatedName && truncatedName.length > 90) {
                estimatedHeight = baseRowHeight * 2; // Twice as tall for very long names
              }

              rows.push({
                data: rowData,
                height: estimatedHeight,
                percentageValue: item.percentageValue,
                securityType: item.securityType,
              });
            });
          }
        });
      }

      // Calculate adaptive pagination
      const usableHeight = availableHeight - titleSpace - headerSpace - safetyMargin;

      // Distribute rows across slides based on cumulative height
      const slides = [];
      let currentSlide = [];
      let currentHeight = headerHeight; // Start with header height

      rows.forEach(row => {
        // Don't break account or security type groups across slides if possible
        const isHeader = row.isAccountSummary || row.isSecurityTypeSummary;
        const nextRowHeight = row.height || baseRowHeight;

        // If this is a header and would exceed available height, start a new slide
        if (isHeader && currentHeight + nextRowHeight > usableHeight) {
          if (currentSlide.length > 0) {
            slides.push(currentSlide);
          }
          currentSlide = [row];
          currentHeight = headerHeight + nextRowHeight;
        } else if (!isHeader && currentHeight + nextRowHeight > usableHeight) {
          // If this is a regular row and would exceed available height, start a new slide
          slides.push(currentSlide);
          currentSlide = [row];
          currentHeight = headerHeight + nextRowHeight;
        } else {
          // Otherwise add to current slide
          currentSlide.push(row);
          currentHeight += nextRowHeight;
        }
      });

      // Add the last slide if it has any rows
      if (currentSlide.length > 0) {
        slides.push(currentSlide);
      }

      // Generate slides
      const numSlides = slides.length;
      console.log(`Creating ${numSlides} slides with professionally formatted ${holdingsType} holdings`);

      // Single unified slide generation loop that includes footnote handling
      slides.forEach((slideRows, slideIndex) => {
        const slide = pres.addSlide();
        this.y = 0;

        // Add corporate branding header
        this.addSlideHeader(slide, 1, 1);

        // Add slide title with pagination if more than one slide
        const slideTitle = numSlides > 1 ?
          `${SLIDE_TITLE} (${slideIndex + 1}/${numSlides})` :
          SLIDE_TITLE;
        this.addSlideTitle(slide, slideTitle, { fontSize: 20 });

        this.y += .2;

        // Extract row data and determine row heights for the table
        const tableRows = slideRows.map(row => row.data);
        const rowHeights = slideRows.map(row => row.height);

        // Create table data with headers
        const tableData = [headers, ...tableRows];

        // Calculate table dimensions
        const tableWidth = SLIDE_WIDTH - MARGINS.left - MARGINS.right;
        // const colCount = headers.length;

        // Determine column widths with custom distribution
        const colWidths = [];

        // Map each column to a relative width factor
        const widthFactors = {
          'Security Type': 1.5,
          'Account/Security Type': 2.0, // Wider to accommodate both account and security type
          'Symbol/CUSIP': 0.9,
          'Name': 3.5, // Name gets more width
          'Pct of Portfolio': 1.5,
          'Pct of Portfolio (Account)': 1.8, // A bit wider to accommodate both percentages
        };

        // Calculate total factors for present columns
        const totalFactor = headers.reduce((sum, header) => sum + (widthFactors[header.text] || 1), 0);

        // Distribute width based on factors
        headers.forEach(header => {
          const factor = widthFactors[header.text] || 1;
          colWidths.push((factor / totalFactor) * tableWidth);
        });

        // Add the table to the slide with variable row heights
        slide.addTable(tableData, {
          x: MARGINS.left,
          y: this.y,
          w: tableWidth,
          rowH: [headerHeight, ...rowHeights], // First row is header, rest are content rows
          colW: colWidths,
          valign: 'middle',
          margin: 0.05,
          autoPage: false, // Disable auto pagination since we're handling it manually
          shadow: { type: 'outer', angle: 45, blur: 3, offset: 1, color: 'CCCCCC', opacity: 0.3 }, // Subtle shadow for 3D effect
        });

        // Note, we would want to probably add the portfolio holdings as of date here
        // if (slideIndex === 0) {
        //   const currentDate = new Date().toLocaleDateString('en-US', {
        //     year: 'numeric',
        //     month: 'long',
        //     day: 'numeric'
        //   });


        //   slide.addText(`As of ${portfolioHoldingsAsOfDate}`, {
        //     x: SLIDE_WIDTH - MARGINS.right - 2.5,
        //     y: SLIDE_HEIGHT - MARGINS.bottom - 0.3,
        //     w: 2.5,
        //     h: 0.3,
        //     align: 'right',
        //     fontSize: 9,
        //     color: '666666'
        //   });
        // }

        // Add the footnote if this is the last slide and proxy asterisks are used
        if (proxyAsterisk && slideIndex === slides.length - 1) {
          slide.addText('* A proxy ticker was assigned to this holding for the purposes of constructing portfolio analytics', {
            x: MARGINS.left,
            y: SLIDE_HEIGHT - MARGINS.bottom - 0.6, // Position above the date stamp
            w: SLIDE_WIDTH - MARGINS.left - MARGINS.right - 2.5, // Leave space for date stamp
            h: 0.3,
            fontSize: 8,
            color: '666666',
            italic: true,
            align: 'left',
          });
        }
      });
    },
    toCurrency(value) {
      if (typeof value === 'string') {
        if (value.startsWith('$')) return value;
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }
      const dollars = value?.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return dollars ? `$${dollars}` : '';
    },
    toPercent(value) {
      if (typeof value === 'string') {
        if (value.endsWith('%')) return value;
        value = parseFloat(value);
        if (isNaN(value)) return '';
      }

      // Use 6 decimal places for very small percentages (less than 0.000001%)
      const maximumFractionDigits = value < 0.000001 ? 6 : value < 0.00001 ? 5 : value < 0.0001 ? 4 : value < 0.0001 ? 3 : 2;

      let percent = value?.toLocaleString('en-US', {
        style: 'percent',
        maximumFractionDigits: maximumFractionDigits,
      });

      // Handle -0% case
      if (percent === '-0%') {
        percent = '0%';
      }

      return percent;
    },
  },
};
</script>